<!--
  RETIRING — DO NOT ADD FEATURES TO THIS TEMPLATE.
  The Book a Meeting panel is being decommissioned; it stays live only as a
  transitional fallback. New meeting features belong in the /schedule event
  panel (sp-meeting-card / sp-meeting-expanded, SchedulePreviewComponent) and
  the Calendar (Beta) side panel (sv3-event-editor / sv3-event-detail).
  See the header of book-meeting-panel.component.ts.
-->
<!-- Floating schedule shortcuts — view mode only. A SIBLING of the panel
     (the panel clips overflow) fixed to the viewport just left of its
     edge: the way FROM a meeting INTO the schedule — land on its day +
     time with the detail open, or replan via Find-a-time. -->
@if (visible() && canOpenInSchedule() && scheduleV2Enabled) {
  <aside class="bmp__hop ph-no-capture" [style.right.px]="panelWidth() + 16" aria-label="Schedule shortcuts">
    <span class="bmp__hop-tag">Schedule</span>
    <paper-button intent="ghost" class="bmp__hop-btn" data-testid="bmp-open-in-schedule" (click)="openInSchedule('view')">
      <paper-icon name="calendar" [size]="14" aria-hidden="true" />
      <span>Open in schedule</span>
    </paper-button>
    <paper-button intent="ghost" class="bmp__hop-btn" data-testid="bmp-replan-find-time" (click)="openInSchedule('replan')">
      <paper-icon name="clock" [size]="14" aria-hidden="true" />
      <span>Replan · find a time</span>
    </paper-button>
  </aside>
}

<!-- Book Meeting Panel — slide-in from right edge -->
<div
  class="bmp"
  role="dialog"
  aria-modal="true"
  [attr.aria-labelledby]="isView() ? null : 'bmp-title-input'"
  [attr.aria-label]="isView() ? 'Meeting details' : null"
  [attr.inert]="!visible() ? '' : null"
  [attr.data-companion-avoid]="visible() ? '' : null"
  [class.show]="visible()"
  [class.bmp--view]="isView() && view() === BookMeetingView.FORM"
  [style.width]="panelInlineSize()"
  [style]="meetingLabelStyle()"
  [class.has-meeting-label]="!!service.meetingLabelColor()"
  [class.resizing]="isResizing()"
  [attr.data-testid]="'book-meeting-panel'"
  [attr.data-view]="view()">

  <!-- Resize drag handle -->
  <div class="bmp__resize-handle" (mousedown)="onResizeStart($event)"></div>

  <!-- Header — chrome differs between view-mode (Space-panel-style title +
       meta strip) and the form-flow (compact heading bar with back button).
       Keeping them as parallel templates avoids a single header trying to
       morph between two visual shapes. -->
  <header class="bmp__header" [class.bmp__header--minimal]="isView() && view() === BookMeetingView.FORM">
    @switch (view()) {
      @case (BookMeetingView.FORM) {
        @if (!isView()) {
          <!-- Schedule V2 — prototype header: title row with optional
               "Draft · autosaves" pill + action group (minimise + close).
               In legacy mode keep the plain title; the panel-level close
               below still renders the × button. -->
          @if (scheduleV2Enabled) {
            <div class="bmp__head-title">
              <h2 class="bmp__title">
                {{ service.subject().trim() || (isEdit() ? 'Edit meeting' : 'New meeting') }}
              </h2>
              @if (service.rooms().length > 1) {
                <span class="bmp__multi-room-badge"
                      data-testid="bmp-multi-room-badge"
                      [title]="service.rooms().length + ' rooms booked together'">
                  <paper-icon name="building" [size]="11" aria-hidden="true" />
                  {{ 'viewer.book_meeting_panel.service_rooms' | transloco:{ service: service.rooms().length } }}
                </span>
              }
              <!-- Edit mode is NOT a draft session — no autosave runs, so the
                   draft pill would lie. Show the editing state instead. -->
              @if (isEdit()) {
                <span class="bmp__draft-pill bmp__draft-pill--editing" aria-live="polite" data-testid="bmp-editing-pill">
                  <paper-icon name="edit" [size]="10" aria-hidden="true" />
                  {{ editDirty() ? 'Editing · unsaved changes' : 'Editing' }}
                </span>
              } @else {
                <span class="bmp__draft-pill" aria-live="polite" data-testid="bmp-draft-pill">
                  <paper-icon name="check" [size]="10" aria-hidden="true" />
                  {{ draftPillLabel() }}
                </span>
              }
            </div>
            <div class="bmp__head-actions">
              <!-- Minimise persists a DRAFT — only meaningful for create
                   sessions; an existing meeting is never a draft. -->
              @if (!isEdit()) {
                <paper-button
                  id="bmp-minimise-btn"
                  class="bmp__nav-btn"
                  intent="ghost" size="sm" shape="icon"
                  icon="minus" [iconSize]="14"
                  [ariaLabel]="'Minimise draft'"
                  [title]="'Minimise draft — keep working elsewhere'"
                  data-testid="bmp-minimise-btn"
                  (click)="onMinimiseDraft()" />
              }
            </div>
          } @else {
            <h2 class="bmp__title">{{ 'viewer.book_meeting_panel.book_a_meeting' | transloco }}</h2>
          }
        }
      }
      @case (BookMeetingView.VENDOR_BROWSE) {
        <paper-button
          class="bmp__nav-btn"
          intent="ghost" size="sm" shape="icon"
          icon="chevron-left" [iconSize]="14"
          [ariaLabel]="'Go back'"
          (click)="service.backToForm()" />
        <h2 class="bmp__title">
          @if (service.activeRoomForCatering(); as room) {
            {{ 'viewer.book_meeting_panel.catering_for_room' | transloco:{ room: room.name } }}
          } @else {
            {{ 'viewer.book_meeting_panel.add_catering' | transloco }}
          }
        </h2>
      }
      @case (BookMeetingView.MENU_BROWSE) {
        <paper-button
          class="bmp__nav-btn"
          intent="ghost" size="sm" shape="icon"
          icon="chevron-left" [iconSize]="14"
          [ariaLabel]="'Go back'"
          (click)="service.navigateTo(BookMeetingView.VENDOR_BROWSE)" />
        <h2 class="bmp__title">{{ 'viewer.book_meeting_panel.menu' | transloco }}</h2>
      }
      @case (BookMeetingView.CART_REVIEW) {
        <paper-button
          class="bmp__nav-btn"
          intent="ghost" size="sm" shape="icon"
          icon="chevron-left" [iconSize]="14"
          [ariaLabel]="'Go back'"
          (click)="service.navigateTo(BookMeetingView.MENU_BROWSE)" />
        <h2 class="bmp__title">{{ 'viewer.book_meeting_panel.review_order' | transloco }}</h2>
      }
      @case (BookMeetingView.EMAIL_REQUEST) {
        <paper-button
          class="bmp__nav-btn"
          intent="ghost" size="sm" shape="icon"
          icon="chevron-left" [iconSize]="14"
          [ariaLabel]="'Go back'"
          (click)="service.navigateTo(BookMeetingView.VENDOR_BROWSE)" />
        <h2 class="bmp__title">{{ 'viewer.book_meeting_panel.send_request' | transloco }}</h2>
      }
      @case (BookMeetingView.CONFIRMATION) {
        <h2 class="bmp__title">{{ 'viewer.book_meeting_panel.confirmed' | transloco }}</h2>
      }
    }
    @if (isView() && view() === BookMeetingView.FORM) {
      <!-- View-mode chrome cluster — top-right icon bar. Share is always
           visible here so the user always has a way to copy the meeting
           link, regardless of whether the floating action shelf is in
           view. Edit pencil shows when the user has permission. -->
      <div class="bmp__view-chrome">
        <paper-button
          intent="ghost" size="sm" shape="icon"
          data-testid="share-meeting-btn"
          [icon]="shareCopied() ? 'check' : 'share'" [iconSize]="14"
          [ariaLabel]="shareCopied() ? 'Link copied' : 'Copy share link'"
          [title]="shareCopied() ? 'Copied!' : 'Copy share link'"
          (click)="onShareMeeting()" />
        @if (service.canEdit()) {
          <paper-button
            intent="ghost" size="sm" shape="icon"
            data-testid="edit-meeting-btn"
            icon="edit" [iconSize]="14"
            [ariaLabel]="'Edit meeting'"
            [title]="'Edit meeting'"
            (click)="onEditMeeting()" />
        }
      </div>
    }
    <paper-button
      class="bmp__nav-btn bmp__nav-btn--close"
      intent="ghost" size="sm" shape="icon"
      icon="close" [iconSize]="14"
      [ariaLabel]="'Close panel'"
      (click)="close()" />
  </header>

  <!-- Schedule V2 — tab bar lives OUTSIDE .bmp__content so it's
       structurally pinned at the top of the panel rather than scroll-
       sticky inside the scroll area. This way every piece of content
       (templates row, form sections) scrolls fully under the bar from
       the very first pixel — no template-label-bleeding-above-the-bar
       glitch on initial render. Gated on V2 + showTabBar(); legacy
       mode keeps its in-scroll mount below. -->
  @if (scheduleV2Enabled && showTabBar() && view() === BookMeetingView.FORM) {
    <bmp-tab-bar
      class="bmp__tab-bar-pinned"
      [activeTab]="activeTab()"
      [counts]="tabCounts()"
      [validation]="tabValidation()"
      [progress]="scrollProgress()"
      (tabChanged)="onTabChanged($event)" />
  }

  <!-- Content — view router -->
  <div class="bmp__content">
    @switch (view()) {
      @case (BookMeetingView.FORM) {
        <!-- Permission-aware chrome banners.
             Loading / load-error / admin-view / admin-edit. Stacked above the
             form so they read first regardless of scroll position. Each is
             gated on `service.mode()` so the create flow renders zero of
             them. -->
        @if (service.loadingMeeting()) {
          <div
            class="bmp__chrome-banner bmp__chrome-banner--loading"
            role="status"
            aria-live="polite">
            <paper-icon name="refresh" [size]="14" class="bmp__btn-spin" />
            <span>{{ 'viewer.book_meeting_panel.loading_meeting' | transloco }}</span>
          </div>
        } @else if (service.loadError(); as loadErr) {
          <div
            class="bmp__chrome-banner bmp__chrome-banner--error"
            role="alert"
            aria-live="assertive">
            <paper-icon name="alert-triangle" [size]="14" />
            <div class="bmp__chrome-banner-text">
              <strong>{{ 'viewer.book_meeting_panel.could_not_load_meeting' | transloco }}</strong>
              <span>{{ loadErr }}</span>
            </div>
            <div class="bmp__chrome-banner-actions">
              <paper-button
                class="bmp__chrome-banner-btn"
                intent="secondary" size="sm"
                (click)="retryLoad()">{{ 'viewer.book_meeting_panel.retry' | transloco }}</paper-button>
              <paper-button
                class="bmp__chrome-banner-btn"
                intent="secondary" size="sm"
                (click)="close()">{{ 'viewer.book_meeting_panel.close' | transloco }}</paper-button>
            </div>
          </div>
        } @else if (showAdminViewBanner()) {
          <div
            class="bmp__chrome-banner bmp__chrome-banner--admin-view"
            role="status"
            aria-live="polite">
            <paper-icon name="shield" [size]="14" />
            <div class="bmp__chrome-banner-text">
              <span>
                <strong>{{ 'viewer.book_meeting_panel.admin_view' | transloco }}</strong> {{ 'viewer.book_meeting_panel.you_re_managing_organizername_s_meeting' | transloco:{ organizerName: organizerName() } }}
              </span>
            </div>
            <span
              class="bmp__chrome-banner-help"
              tabindex="0"
              role="img"
              [attr.aria-label]="'viewer.book_meeting_panel.you_have_admin_permission_booking_update' | transloco"
              [title]="'viewer.book_meeting_panel.you_have_admin_permission_booking_update_2' | transloco">
              <paper-icon name="help-circle" [size]="13" />
            </span>
          </div>
        } @else if (showAdminEditBanner()) {
          <div
            class="bmp__chrome-banner bmp__chrome-banner--admin-edit"
            role="alert"
            aria-live="polite">
            <paper-icon name="alert-triangle" [size]="14" />
            <div class="bmp__chrome-banner-text">
              <span>
                <strong>{{ 'viewer.book_meeting_panel.editing_as_admin' | transloco }}</strong> {{ 'viewer.book_meeting_panel.changes_will_notify_organizername' | transloco:{ organizerName: organizerName() } }}
              </span>
            </div>
          </div>
        }

        <!-- Error banner -->
        @if (service.submitError()) {
          <div
            class="bmp__error-banner"
            [class.bmp__error-banner--calendar-rejected]="service.submitErrorIsCalendarReject()"
            role="alert"
            aria-live="assertive">
            <paper-icon name="alert-triangle" [size]="14" />
            <div class="bmp__error-text">
              @if (service.submitErrorIsCalendarReject()) {
                <strong class="bmp__error-headline">
                  {{ 'viewer.book_meeting_panel.calendar_rejected_the_meeting' | transloco }}
                </strong>
              }
              <span>{{ service.submitError() }}</span>
              @if (service.submitErrorIsCalendarReject()) {
                <span class="bmp__error-hint">
                  {{ 'viewer.book_meeting_panel.try_a_different_room_or_check' | transloco }}
                </span>
              }
            </div>
            <paper-button
              class="bmp__error-dismiss"
              intent="ghost" shape="icon" size="xs"
              icon="x" [iconSize]="12"
              [ariaLabel]="'viewer.book_meeting_panel.dismiss' | transloco"
              (click)="service.submitError.set(null)" />
          </div>
        }

        <!-- Tab bar — only in create / edit mode. View mode keeps its
             hero-style layout from the polished pass; tabs would fight
             the eyebrow + big title there. The bar's validation chips
             draw the eye to the tab owning a missing required field
             when the user hits a disabled Book Meeting button. -->
        @if (showTabBar() && !scheduleV2Enabled) {
          <!-- Legacy in-scroll tab bar — V2 uses the structurally
               pinned bar above .bmp__content instead. -->
          <bmp-tab-bar
            [activeTab]="activeTab()"
            [counts]="tabCounts()"
            [validation]="tabValidation()"
            [progress]="scrollProgress()"
            (tabChanged)="onTabChanged($event)" />
        }

        <!-- Schedule V2 — templates row sits at the top of the scroll
             area, just under the pinned tab bar. Mirrors the
             prototype's `bp-templates` block. Gated behind
             scheduleV2Enabled so prod rollout can flip it on without
             a redeploy. -->
        @if (scheduleV2Enabled && showTabBar()) {
          <bmp-templates-row
            [appliedId]="service.meetingTemplateId()"
            [canSaveAsTemplate]="!!service.subject().trim()"
            (templateApplied)="onTemplateApplied($event)"
            (templateCleared)="onTemplateCleared()"
            (saveAsTemplate)="onSaveAsTemplate()" />
        }

        <!-- Schedule V2 — two-column body wrapper. The left main pane
             holds the existing form/view sections; the right rail
             pins the AI side panel across every tab. Falls back to a
             single-column layout when `scheduleV2Enabled` is off (and
             always for view mode, where the side rail would compete
             with the hero header). -->
        <div class="bmp__v2-body"
          [class.bmp__v2-body--single]="!scheduleV2Enabled || isView()">
        <div class="bmp__v2-main">
        @if (isView()) {
          <!-- View-mode header — Space-panel-style hierarchy with eyebrow +
               big title + dot-separated meta strip. Replaces the form's
               meeting-details + date-time sections (those are editing
               surfaces; view mode is a summary). -->
          <section class="bmp__view-header" data-testid="view-header">
            <span class="bmp__eyebrow">{{ viewModeEyebrow() }}</span>
            <h1
              class="bmp__title-large"
              [class.bmp__title-large--empty]="!service.subject() && !service.isPrivate()">
              @if (service.subject()) {
                {{ service.subject() }}
              } @else if (service.isPrivate()) {
                {{ 'viewer.book_meeting_panel.private_meeting' | transloco }}
              } @else if (service.loadedOrganizerName() && !service.viewerIsOrganizer()) {
                {{ 'viewer.book_meeting_panel.service_s_meeting' | transloco:{ service: service.loadedOrganizerName() } }}
              } @else {
                {{ 'viewer.book_meeting_panel.no_subject' | transloco }}
              }
            </h1>
            <div class="bmp__meta">
              @if (categoryChipLabel(); as chipLabel) {
                <span
                  class="bmp__category-chip"
                  data-testid="category-chip"
                  [style]="categoryChipStyle()">
                  {{ chipLabel }}
                </span>
              }
              @if (viewModeDateLabel()) {
                <span class="bmp__meta-item">
                  <paper-icon name="calendar" [size]="12" />
                  {{ viewModeDateLabel() }}
                </span>
              }
              @if (viewModeTimeLabel()) {
                <span class="bmp__meta-dot"></span>
                <span class="bmp__meta-item">
                  <paper-icon name="clock" [size]="12" />
                  {{ viewModeTimeLabel() }}
                </span>
              }
              @if (viewModeRoomLabel()) {
                <span class="bmp__meta-dot"></span>
                <span class="bmp__meta-item">
                  <paper-icon name="map-pin" [size]="12" />
                  {{ viewModeRoomLabel() }}
                </span>
              }
            </div>

            <!-- Classify row in view-mode — same shared component
                 as create-mode (bmp-classify-row). Category + tags
                 changes route through the viewer-overlay save
                 mutations so they're scoped to THIS viewer and never
                 written to the source Booking or other attendees'
                 records. Flow project toggle routes through the
                 existing attachProjectToOverlay path so the viewer
                 can group invited meetings under their own Flow
                 projects. -->
            <div class="bmp__view-classify" data-testid="view-classify-row">
              <bmp-classify-row
                [categoryId]="service.meetingCategoryId()"
                [tags]="service.meetingTags()"
                [flowProjectId]="viewModeActiveFlowProjectId()"
                [flowProjects]="flowProjects.activeProjects()"
                mode="view"
                (categoryChanged)="onViewClassifyCategoryChanged($event)"
                (tagsChanged)="onViewClassifyTagsChanged($event)"
                (flowProjectChanged)="onViewClassifyFlowChanged($event)"
                (newProjectRequested)="onViewClassifyNewProject($event)"
                (flowPickerOpened)="onClassifyFlowPickerOpened()" />
            </div>

            <!-- Inline project picker — single instance per panel,
                 floats below the chip cluster. ESC + cancel button
                 close. Uses the same right-tinted popover chrome as
                 the speaker rename surface. -->
            @if (projectPickerOpen()) {
              <div
                class="bmp__project-popover"
                data-testid="project-picker"
                (keydown.escape)="closeProjectPicker()">
                <div class="bmp__project-popover-header">
                  <span class="bmp__project-popover-title">
                    {{ 'viewer.book_meeting_panel.tag_this_meeting_into_a_project' | transloco }}
                  </span>
                  <paper-button
                    class="bmp__project-popover-close"
                    intent="ghost" shape="icon" size="xs"
                    icon="x" [iconSize]="12"
                    [ariaLabel]="'viewer.book_meeting_panel.cancel' | transloco"
                    (click)="closeProjectPicker()" />
                </div>

                <input
                  type="text"
                  class="bmp__project-popover-search"
                  [placeholder]="'viewer.book_meeting_panel.search_projects' | transloco"
                  [value]="projectPickerQuery()"
                  (input)="projectPickerQuery.set($any($event.target).value)" />

                <!-- Phase 3: recurring-series opt-in. Only renders
                     when the loaded meeting belongs to a series with
                     siblings. Toggling it changes the next add to
                     back-fill all instances. -->
                @if (hasMeetingSeries(); as _) {
                  @let info = meetingSeriesInfo();
                  <label class="bmp__project-popover-series">
                    <input
                      type="checkbox"
                      [checked]="includeSeriesOptIn()"
                      (change)="includeSeriesOptIn.set($any($event.target).checked)" />
                    <span class="bmp__project-popover-series-text">
                      <span class="bmp__project-popover-series-title">
                        Include all {{ info?.totalCount }} instances
                      </span>
                      <span class="bmp__project-popover-series-meta">
                        {{ 'viewer.book_meeting_panel.recurring_series_past_future_occurrences_will' | transloco }}
                      </span>
                    </span>
                  </label>
                }

                @let suggestions = projectPickerSuggestions();
                @if (suggestions.length === 0 && !projectPickerCreating()) {
                  <p class="bmp__project-popover-empty">
                    @if (projectPickerQuery()) {
                      {{ 'viewer.book_meeting_panel.no_projects_match_projectpickerquery' | transloco:{ projectPickerQuery: projectPickerQuery() } }}
                    } @else {
                      {{ 'viewer.book_meeting_panel.you_don_t_have_any_projects' | transloco }}
                    }
                  </p>
                } @else {
                  <ul class="bmp__project-popover-list" role="listbox">
                    @for (project of suggestions; track project._id) {
                      @let attached = isProjectAttached(project._id);
                      <li>
                        <button
                          type="button"
                          class="bmp__project-popover-option"
                          [class.bmp__project-popover-option--attached]="attached"
                          [attr.aria-pressed]="attached"
                          (click)="toggleProjectAttachment(project._id)">
                          <paper-icon
                            [name]="attached ? 'check-square' : 'square'"
                            [size]="14" />
                          <span class="bmp__project-popover-option-text">
                            <span class="bmp__project-popover-option-name">
                              @if (project.icon) {
                                <span>{{ project.icon }}</span>
                              }
                              {{ project.name }}
                            </span>
                            <span class="bmp__project-popover-option-meta">
                              {{ project.meetingCount }}
                              {{ project.meetingCount === 1 ? 'meeting' : 'meetings' }}
                            </span>
                          </span>
                        </button>
                      </li>
                    }
                  </ul>
                }

                @if (projectPickerCreating()) {
                  <div class="bmp__project-popover-create">
                    <input
                      type="text"
                      class="bmp__project-popover-search"
                      [placeholder]="'viewer.book_meeting_panel.project_name' | transloco"
                      [value]="projectPickerNewName()"
                      (input)="projectPickerNewName.set($any($event.target).value)"
                      (keydown.enter)="commitNewProject()" />
                    <div class="bmp__project-popover-actions">
                      <paper-button
                        class="bmp__project-popover-cancel"
                        intent="ghost" size="xs"
                        (click)="projectPickerCreating.set(false)">
                        {{ 'viewer.book_meeting_panel.cancel_2' | transloco }}
                      </paper-button>
                      <paper-button
                        class="bmp__project-popover-save"
                        intent="primary" size="xs"
                        [disabled]="!projectPickerNewName().trim()"
                        (click)="commitNewProject()">
                        {{ 'viewer.book_meeting_panel.create_attach' | transloco }}
                      </paper-button>
                    </div>
                  </div>
                } @else {
                  <button
                    type="button"
                    class="bmp__project-popover-new"
                    (click)="projectPickerCreating.set(true); projectPickerNewName.set(projectPickerQuery())">
                    <paper-icon name="plus" [size]="11" />
                    {{ 'viewer.book_meeting_panel.create_new_project' | transloco }}
                    @if (projectPickerQuery()) {
                      <span class="bmp__project-popover-new-hint">
                        {{ 'viewer.book_meeting_panel.projectpickerquery' | transloco:{ projectPickerQuery: projectPickerQuery() } }}
                      </span>
                    }
                  </button>
                }
              </div>
            }

            <!-- Identifier row — surfaces the share link plus the Teams
                 Meeting ID (when extracted from the body) as discrete copy
                 affordances. Anyone who needs to forward this meeting to
                 someone joining from a phone or non-clickable surface can
                 copy the ID without scrolling into the JOIN MEETING card. -->
            <div class="bmp__id-row" data-testid="id-row">
              @if (service.loadedMeetingId() || service.loadedBookingId()) {
                <button
                  type="button"
                  class="bmp__id-chip"
                  data-testid="share-link-chip"
                  [attr.aria-label]="shareCopied() ? 'Link copied' : 'Copy share link'"
                  (click)="onShareMeeting()">
                  <paper-icon [name]="shareCopied() ? 'check' : 'link'" [size]="11" />
                  <span>{{ shareCopied() ? 'Copied' : 'Copy link' }}</span>
                </button>
              }
              @if (parsedBody().meetingId; as id) {
                <button
                  type="button"
                  class="bmp__id-chip bmp__id-chip--monospace"
                  data-testid="meeting-id-chip"
                  [attr.aria-label]="meetingIdCopied() ? 'Meeting ID copied' : 'Copy Meeting ID'"
                  (click)="onCopyMeetingId(id)">
                  <paper-icon [name]="meetingIdCopied() ? 'check' : 'copy'" [size]="11" />
                  <span>{{ 'viewer.book_meeting_panel.id_id' | transloco:{ id: id } }}</span>
                </button>
              }
            </div>
          </section>

          <!-- GOING? status card — left side shows the current state, right
               side a 3-button choice grid. Only renders for non-organisers
               with a known response status; organisers don't RSVP to their
               own meetings. -->
          @if (showGoingCard()) {
            <section
              class="bmp__rsvp-card"
              role="group"
              [attr.aria-label]="'viewer.book_meeting_panel.your_response' | transloco"
              data-testid="going-card">
              <div class="bmp__rsvp-status">
                <span class="bmp__rsvp-pill" [attr.data-tone]="goingPillTone()">
                  <span class="bmp__rsvp-dot"></span>
                  {{ goingPillLabel() }}
                </span>
              </div>
              <div class="bmp__rsvp-choices">
                <button
                  type="button"
                  class="bmp__rsvp-choice"
                  [class.bmp__rsvp-choice--active]="service.viewerResponseStatus() === 'accepted'"
                  [disabled]="service.rsvpSubmitting()"
                  (click)="onRsvp('accepted')">
                  {{ 'viewer.book_meeting_panel.yes' | transloco }}
                </button>
                <button
                  type="button"
                  class="bmp__rsvp-choice"
                  [class.bmp__rsvp-choice--active]="service.viewerResponseStatus() === 'tentativelyAccepted'"
                  [disabled]="service.rsvpSubmitting()"
                  (click)="onRsvp('tentative')">
                  {{ 'viewer.book_meeting_panel.maybe' | transloco }}
                </button>
                <button
                  type="button"
                  class="bmp__rsvp-choice"
                  [class.bmp__rsvp-choice--active]="service.viewerResponseStatus() === 'declined'"
                  [disabled]="service.rsvpSubmitting()"
                  (click)="onRsvp('declined')">
                  {{ 'viewer.book_meeting_panel.no' | transloco }}
                </button>
              </div>
            </section>
          }

          <!-- FLOW RECORDINGS — unified section. Renders for ANY meeting
               (past, present, future) — there's nothing stopping someone
               recording a future meeting, a memo, a conversation. Source
               of truth is `service.flowLinks()` (server-side join via
               UserCalendarMeetings.flowLinks[]). Each card pulls rich
               brief + topics from `flowDetailFor()` (lazy enrichment per
               link), and opens the recording in the web reader,
               `/flow/sessions/:id`.

               SESSION REPLAY: everything a recording says is masked here —
               speakers, the brief, topics, insights, the transcript, notes,
               meeting subjects, project and workspace names. ph-block where
               the same content is also in an attribute or an image (a
               speaker's name in `title`, a photo's `src`), because replay
               records attributes verbatim under ph-mask.

               ANALYTICS: ph-no-capture on the section. Its rows are buttons
               labelled with meeting subjects and project names, and
               posthog-js folds a nested span's text into the button's own,
               where ph-mask cannot reach it (tv45.100). -->
          @if (service.flowLinks().length > 0) {
            <section class="bmp__flow-section ph-no-capture" data-testid="flow-section">
              <div class="bmp__section-label">
                <paper-icon name="play" [size]="12" />
                <span>
                  {{ 'viewer.book_meeting_panel.recorded_with_flow' | transloco }}
                  @if (service.flowLinks().length > 1) {
                    · {{ service.flowLinks().length }}
                  }
                </span>
              </div>
              @for (flow of service.flowLinks(); track flow.flowMeetingId) {
                @let detail = flowDetailFor(flow.flowMeetingMeetingId);
                @let blocks = detailedSummaryBlocks(detail);
                @let transcriptOpen = isFlowSectionOpen(flow.flowMeetingMeetingId, FlowRevealSection.TRANSCRIPT);
                @let insightsOpen = isFlowSectionOpen(flow.flowMeetingMeetingId, FlowRevealSection.INSIGHTS);
                @let notesOpen = isFlowSectionOpen(flow.flowMeetingMeetingId, FlowRevealSection.NOTES);
                @let notesPresent = hasNotes(detail);
                @let transcriptQ = transcriptQuery(flow.flowMeetingMeetingId);
                @let filteredSegs = filteredTranscript(detail, flow.flowMeetingMeetingId);
                @let knownSpeakers = namedSpeakers(detail);
                @let speakersLabel = speakersHeaderText(flow, detail);
                <article class="bmp__flow-card" data-testid="flow-card">
                  <header class="bmp__flow-card-header">
                    @if (knownSpeakers.length > 0) {
                      <!-- Avatar stack — proof the system knows who was
                           in the recording. Up to 3 avatars overlap; if
                           more, the last slot becomes a "+N" pill. -->
                      <div
                        class="bmp__flow-card-avatars ph-block"
                        [attr.data-count]="knownSpeakers.length"
                        aria-hidden="true">
                        @for (sp of knownSpeakers.slice(0, AVATAR_STACK_LIMIT); track sp.speakerId) {
                          @if (sp.avatar) {
                            <img
                              class="bmp__flow-card-avatar"
                              [src]="sp.avatar"
                              [alt]=""
                              [title]="sp.name" />
                          } @else {
                            <span
                              class="bmp__flow-card-avatar bmp__flow-card-avatar--initials"
                              [title]="sp.name">
                              {{ (sp.name || '?').charAt(0).toUpperCase() }}
                            </span>
                          }
                        }
                        @if (knownSpeakers.length > AVATAR_STACK_LIMIT) {
                          <span class="bmp__flow-card-avatar bmp__flow-card-avatar--more">
                            +{{ knownSpeakers.length - AVATAR_STACK_LIMIT }}
                          </span>
                        }
                      </div>
                    } @else {
                      <div class="bmp__flow-card-icon" aria-hidden="true">
                        <paper-icon name="mic" [size]="16" />
                      </div>
                    }
                    <div class="bmp__flow-card-summary">
                      <div class="bmp__flow-card-title">
                        {{ formatFlowDuration(flow.recordingDurationSeconds) }}
                        @if (speakersLabel) {
                          <span class="bmp__flow-card-meta ph-mask">· {{ speakersLabel }}</span>
                        }
                      </div>
                      <div class="bmp__flow-card-tags">
                        @if (flow.hasTranscript && (detail?.transcript?.length ?? 0) > 0) {
                          <button
                            type="button"
                            class="bmp__flow-tag bmp__flow-tag--button"
                            [class.bmp__flow-tag--active]="transcriptOpen"
                            [attr.aria-pressed]="transcriptOpen"
                            (click)="toggleFlowSection(flow.flowMeetingMeetingId, FlowRevealSection.TRANSCRIPT)">
                            <paper-icon name="file-text" [size]="11" />
                            {{ 'viewer.book_meeting_panel.transcript' | transloco }}
                            <paper-icon
                              [name]="transcriptOpen ? 'chevron-up' : 'chevron-down'"
                              [size]="11" />
                          </button>
                        } @else if (flow.hasTranscript) {
                          <span class="bmp__flow-tag">
                            <paper-icon name="file-text" [size]="11" /> {{ 'viewer.book_meeting_panel.transcript_2' | transloco }}
                          </span>
                        }
                        @if (flow.hasAiSummary && blocks.length > 0) {
                          <button
                            type="button"
                            class="bmp__flow-tag bmp__flow-tag--button"
                            [class.bmp__flow-tag--active]="insightsOpen"
                            [attr.aria-pressed]="insightsOpen"
                            (click)="toggleFlowSection(flow.flowMeetingMeetingId, FlowRevealSection.INSIGHTS)">
                            <paper-icon name="sparkles" [size]="11" />
                            {{ 'viewer.book_meeting_panel.ai_insights' | transloco }}
                            <paper-icon
                              [name]="insightsOpen ? 'chevron-up' : 'chevron-down'"
                              [size]="11" />
                          </button>
                        } @else if (flow.hasAiSummary) {
                          <span class="bmp__flow-tag">
                            <paper-icon name="sparkles" [size]="11" /> {{ 'viewer.book_meeting_panel.ai_insights_2' | transloco }}
                          </span>
                        }
                        @if (notesPresent) {
                          <button
                            type="button"
                            class="bmp__flow-tag bmp__flow-tag--button"
                            [class.bmp__flow-tag--active]="notesOpen"
                            [attr.aria-pressed]="notesOpen"
                            (click)="toggleFlowSection(flow.flowMeetingMeetingId, FlowRevealSection.NOTES)">
                            <paper-icon name="edit" [size]="11" />
                            {{ 'viewer.book_meeting_panel.notes_2' | transloco }}
                            <paper-icon
                              [name]="notesOpen ? 'chevron-up' : 'chevron-down'"
                              [size]="11" />
                          </button>
                        }
                      </div>
                    </div>
                  </header>

                  @if (detail?.summaryBrief; as brief) {
                    <p class="bmp__flow-brief ph-mask">{{ brief }}</p>
                  }
                  @if ((detail?.topics?.length ?? 0) > 0) {
                    <div class="bmp__flow-topics ph-mask">
                      @for (topic of detail!.topics; track topic) {
                        <span class="bmp__flow-topic-pill">{{ topic }}</span>
                      }
                    </div>
                  }

                  <!-- Cross-recording continuity — recurring-series
                       siblings + project siblings. Renders only when
                       the server returned a payload (one-off meetings
                       with no project tagging get null and skip the
                       whole block). -->
                  @let continuity = continuityFor(flow.flowMeetingMeetingId);
                  @if (continuity) {
                    <section class="bmp__flow-continuity" data-testid="flow-continuity">
                      <!-- Recurring series occurrences. seriesKey is
                           null for one-off meetings even when projects
                           exist, so we gate on it explicitly. -->
                      @if (continuity.seriesKey && continuity.occurrences.length > 1) {
                        <div class="bmp__flow-continuity-section">
                          <div class="bmp__flow-continuity-label">
                            <paper-icon name="repeat" [size]="11" />
                            Part of recurring series ·
                            {{ continuity.totalCount - 1 }}
                            {{ continuity.totalCount - 1 === 1 ? 'other recording' : 'other meetings' }}
                          </div>
                          <ul class="bmp__flow-continuity-list">
                            @for (occ of continuity.occurrences; track occ.meetingId) {
                              @if (!occ.isCurrent) {
                                <li>
                                  <button
                                    type="button"
                                    class="bmp__flow-continuity-row"
                                    (click)="service.loadForView(occ.meetingId)">
                                    <span class="bmp__flow-continuity-row-time">
                                      {{ formatOccurrenceTime(occ.startTime) }}
                                    </span>
                                    <span class="bmp__flow-continuity-row-subject ph-mask">
                                      {{ occ.subject || 'Untitled meeting' }}
                                    </span>
                                    @if (occ.hasFlowRecording) {
                                      <span class="bmp__flow-continuity-row-badge">
                                        <paper-icon name="mic" [size]="9" /> {{ 'viewer.book_meeting_panel.recorded' | transloco }}
                                      </span>
                                    }
                                  </button>
                                </li>
                              }
                            }
                          </ul>
                          @if (continuity.hasMore) {
                            <p class="bmp__flow-continuity-more">
                              {{ 'viewer.book_meeting_panel.continuity_earlier' | transloco:{ continuity: continuity.totalCount - continuity.occurrences.length } }}
                            </p>
                          }
                        </div>
                      }

                      <!-- Project siblings — one block per project this
                           meeting is in. Each block lists up to 5
                           sibling meetings inline. -->
                      @for (proj of continuity.projects; track proj.projectId) {
                        <div class="bmp__flow-continuity-section">
                          <div class="bmp__flow-continuity-label">
                            <paper-icon name="folder" [size]="11" />
                            <button
                              type="button"
                              class="bmp__flow-continuity-project-link ph-mask"
                              (click)="openProjectDetail(proj.projectId)">
                              {{ proj.projectName }}
                            </button>
                            <span>
                              ·
                              {{ proj.totalCount }}
                              {{ proj.totalCount === 1 ? 'other meeting' : 'other meetings' }}
                            </span>
                          </div>
                          @if (proj.siblingMeetings.length > 0) {
                            <ul class="bmp__flow-continuity-list">
                              @for (sib of proj.siblingMeetings; track sib.meetingId) {
                                <li>
                                  <button
                                    type="button"
                                    class="bmp__flow-continuity-row"
                                    (click)="service.loadForView(sib.meetingId)">
                                    <span class="bmp__flow-continuity-row-time">
                                      {{ formatOccurrenceTime(sib.startTime) }}
                                    </span>
                                    <span class="bmp__flow-continuity-row-subject ph-mask">
                                      {{ sib.subject || 'Untitled meeting' }}
                                    </span>
                                    @if (sib.hasFlowRecording) {
                                      <span class="bmp__flow-continuity-row-badge">
                                        <paper-icon name="mic" [size]="9" /> {{ 'viewer.book_meeting_panel.recorded_2' | transloco }}
                                      </span>
                                    }
                                  </button>
                                </li>
                              }
                            </ul>
                          }
                        </div>
                      }
                    </section>
                  }

                  @if (insightsOpen && blocks.length > 0) {
                    <section class="bmp__flow-reveal" data-testid="flow-insights">
                      @for (block of blocks; track block.heading) {
                        @let actionable = isActionableHeading(block.heading);
                        <div class="bmp__flow-block">
                          <h4 class="bmp__flow-block-heading ph-mask">{{ block.heading }}</h4>
                          <ul
                            class="bmp__flow-block-list"
                            [class.bmp__flow-block-list--actionable]="actionable">
                            @for (item of block.items; track item; let i = $index) {
                              <li class="bmp__flow-block-item">
                                <span class="bmp__flow-block-text ph-mask">{{ item }}</span>
                                @if (actionable) {
                                  @let copied = isActionCopied(flow.flowMeetingMeetingId, i);
                                  <button
                                    type="button"
                                    class="bmp__flow-action-copy"
                                    [class.bmp__flow-action-copy--copied]="copied"
                                    [attr.aria-label]="copied ? 'Copied' : 'Copy as Linear task'"
                                    [title]="copied ? 'Copied to clipboard' : 'Copy as Linear-friendly task'"
                                    (click)="copyActionItem(flow, item, i)">
                                    <paper-icon
                                      [name]="copied ? 'check' : 'copy'"
                                      [size]="11" />
                                    {{ copied ? 'Copied' : 'Copy' }}
                                  </button>
                                }
                              </li>
                            }
                          </ul>
                        </div>
                      }
                    </section>
                  }

                  @if (transcriptOpen && (detail?.transcript?.length ?? 0) > 0) {
                    @let editingSpeakerId = editingSpeakerIdFor(flow.flowMeetingMeetingId);
                    @let popoverOpen = isAnySpeakerEditingForFlow(flow.flowMeetingMeetingId);
                    <section class="bmp__flow-reveal" data-testid="flow-transcript">
                      <!-- Single per-card popover. Renders once at the top
                           of the pane (not per segment) so renaming Speaker 2
                           doesn't open six popovers when six segments share
                           that pseudonym. -->
                      @if (popoverOpen) {
                        @let suggestions = speakerSuggestions();
                        @let editingLabel = editingSpeakerLabelFor(flow.flowMeetingMeetingId, detail);
                        <div class="bmp__flow-speaker-popover ph-block" data-testid="speaker-rename">
                          <div class="bmp__flow-speaker-header">
                            <span class="bmp__flow-speaker-headline">
                              {{ 'viewer.book_meeting_panel.renaming' | transloco }} <strong>{{ editingLabel }}</strong>
                            </span>
                            <paper-button
                              class="bmp__flow-speaker-close"
                              intent="ghost" shape="icon" size="xs"
                              icon="x" [iconSize]="12"
                              [ariaLabel]="'viewer.book_meeting_panel.cancel_rename' | transloco"
                              (click)="cancelRenameSpeaker()" />
                          </div>
                          <input
                            type="text"
                            class="bmp__flow-speaker-input"
                            [placeholder]="'viewer.book_meeting_panel.type_a_name_or_pick_from' | transloco"
                            [value]="speakerEditQuery()"
                            (input)="setSpeakerEditQuery($any($event.target).value)"
                            (keydown.enter)="commitRenameSpeaker(flow.flowMeetingMeetingId, editingSpeakerId, speakerEditQuery())"
                            (keydown.escape)="cancelRenameSpeaker()" />

                          @if (suggestions.attendees.length > 0) {
                            <div class="bmp__flow-speaker-section">
                              <div class="bmp__flow-speaker-section-label">{{ 'viewer.book_meeting_panel.in_this_meeting' | transloco }}</div>
                              <ul class="bmp__flow-speaker-list" role="listbox">
                                @for (att of suggestions.attendees; track att.id) {
                                  <li>
                                    <button
                                      type="button"
                                      class="bmp__flow-speaker-option"
                                      (click)="commitRenameSpeakerFromSuggestion(flow.flowMeetingMeetingId, editingSpeakerId, att)">
                                      @if (att.avatar) {
                                        <img class="bmp__flow-speaker-avatar" [src]="att.avatar" [alt]="" />
                                      } @else {
                                        <span class="bmp__flow-speaker-avatar bmp__flow-speaker-avatar--initials">
                                          {{ (att.name || '?').charAt(0).toUpperCase() }}
                                        </span>
                                      }
                                      <span class="bmp__flow-speaker-option-text">
                                        <span class="bmp__flow-speaker-option-name">{{ att.name }}</span>
                                        @if (att.subtitle) {
                                          <span class="bmp__flow-speaker-option-subtitle">{{ att.subtitle }}</span>
                                        }
                                      </span>
                                      <paper-icon name="user" [size]="12" />
                                    </button>
                                  </li>
                                }
                              </ul>
                            </div>
                          }

                          @if (suggestions.directory.length > 0) {
                            <div class="bmp__flow-speaker-section">
                              <div class="bmp__flow-speaker-section-label">{{ 'viewer.book_meeting_panel.other_people' | transloco }}</div>
                              <ul class="bmp__flow-speaker-list" role="listbox">
                                @for (person of suggestions.directory; track person.id) {
                                  <li>
                                    <button
                                      type="button"
                                      class="bmp__flow-speaker-option"
                                      (click)="commitRenameSpeakerFromSuggestion(flow.flowMeetingMeetingId, editingSpeakerId, person)">
                                      @if (person.avatar) {
                                        <img class="bmp__flow-speaker-avatar" [src]="person.avatar" [alt]="" />
                                      } @else {
                                        <span class="bmp__flow-speaker-avatar bmp__flow-speaker-avatar--initials">
                                          {{ (person.name || '?').charAt(0).toUpperCase() }}
                                        </span>
                                      }
                                      <span class="bmp__flow-speaker-option-text">
                                        <span class="bmp__flow-speaker-option-name">{{ person.name }}</span>
                                        @if (person.subtitle) {
                                          <span class="bmp__flow-speaker-option-subtitle">{{ person.subtitle }}</span>
                                        }
                                      </span>
                                      <paper-icon name="user" [size]="12" />
                                    </button>
                                  </li>
                                }
                              </ul>
                            </div>
                          }

                          @if (suggestions.attendees.length === 0 && suggestions.directory.length === 0 && speakerEditQuery().trim()) {
                            <p class="bmp__flow-speaker-no-match">
                              {{ 'viewer.book_meeting_panel.no_matches_press_save_to_use' | transloco }}<strong>{{ speakerEditQuery() }}</strong>{{ 'viewer.book_meeting_panel.as_a_label' | transloco }}
                            </p>
                          }

                          <div class="bmp__flow-speaker-actions">
                            <paper-button
                              class="bmp__flow-speaker-cancel"
                              intent="ghost" size="xs"
                              (click)="cancelRenameSpeaker()">
                              {{ 'viewer.book_meeting_panel.cancel_3' | transloco }}
                            </paper-button>
                            <paper-button
                              class="bmp__flow-speaker-save"
                              intent="primary" size="xs"
                              [disabled]="!speakerEditQuery().trim()"
                              (click)="commitRenameSpeaker(flow.flowMeetingMeetingId, editingSpeakerId, speakerEditQuery())">
                              {{ 'viewer.book_meeting_panel.save_as_text' | transloco }}
                            </paper-button>
                          </div>
                        </div>
                      }

                      <div class="bmp__flow-search">
                        <paper-icon name="search" [size]="12" />
                        <input
                          type="text"
                          class="bmp__flow-search-input"
                          [placeholder]="'viewer.book_meeting_panel.search_transcript' | transloco"
                          [value]="transcriptQ"
                          (input)="setTranscriptQuery(flow.flowMeetingMeetingId, $any($event.target).value)"
                          (keydown.escape)="setTranscriptQuery(flow.flowMeetingMeetingId, '')" />
                        @if (transcriptQ) {
                          <span class="bmp__flow-search-count">
                            {{ filteredSegs.length }}
                            {{ filteredSegs.length === 1 ? 'match' : 'matches' }}
                          </span>
                          <button
                            type="button"
                            class="bmp__flow-search-clear"
                            [attr.aria-label]="'viewer.book_meeting_panel.clear_search' | transloco"
                            (click)="setTranscriptQuery(flow.flowMeetingMeetingId, '')">
                            <paper-icon name="x" [size]="11" />
                          </button>
                        }
                      </div>

                      @if (filteredSegs.length === 0 && transcriptQ) {
                        <p class="bmp__flow-search-empty ph-mask">{{ 'viewer.book_meeting_panel.no_matches_for_transcriptq' | transloco:{ transcriptQ: transcriptQ } }}</p>
                      }

                      @for (segment of filteredSegs; track $index) {
                        @let segmentEditing = segment.speakerId && editingSpeakerId === segment.speakerId;
                        <div class="bmp__flow-segment ph-mask">
                          <div class="bmp__flow-segment-meta">
                            @if (segment.speakerLabel) {
                              <button
                                type="button"
                                class="bmp__flow-segment-speaker bmp__flow-segment-speaker--button"
                                [class.bmp__flow-segment-speaker--editing]="segmentEditing"
                                [disabled]="!segment.speakerId"
                                [title]="segment.speakerId ? 'Click to rename speaker' : ''"
                                (click)="beginRenameSpeaker(flow.flowMeetingMeetingId, segment.speakerId, segment.speakerLabel)">
                                {{ segment.speakerLabel }}
                                @if (segment.speakerId) {
                                  <paper-icon name="edit" [size]="9" />
                                }
                              </button>
                            }
                            @if (formatSegmentTime(segment.startTime); as ts) {
                              <span class="bmp__flow-segment-time">{{ ts }}</span>
                            }
                          </div>
                          <p class="bmp__flow-segment-text">
                            @for (part of highlightedSegmentParts(segment.text, flow.flowMeetingMeetingId); track $index) {
                              @if (part.isMatch) {
                                <mark class="bmp__flow-segment-mark">{{ part.text }}</mark>
                              } @else {
                                <span>{{ part.text }}</span>
                              }
                            }
                          </p>
                        </div>
                      }
                    </section>
                  }

                  @if (notesOpen && notesPresent) {
                    <section class="bmp__flow-reveal" data-testid="flow-notes">
                      @if (hasBothNoteVariants(detail)) {
                        @let variant = noteVariant(flow.flowMeetingMeetingId, detail);
                        <div class="bmp__flow-note-toggle" role="tablist">
                          <button
                            type="button"
                            class="bmp__flow-note-toggle-pill"
                            [class.bmp__flow-note-toggle-pill--active]="variant === FlowNoteVariant.ENHANCED"
                            [attr.aria-pressed]="variant === FlowNoteVariant.ENHANCED"
                            (click)="setNoteVariant(flow.flowMeetingMeetingId, FlowNoteVariant.ENHANCED)">
                            {{ 'viewer.book_meeting_panel.enhanced' | transloco }}
                          </button>
                          <button
                            type="button"
                            class="bmp__flow-note-toggle-pill"
                            [class.bmp__flow-note-toggle-pill--active]="variant === FlowNoteVariant.RAW"
                            [attr.aria-pressed]="variant === FlowNoteVariant.RAW"
                            (click)="setNoteVariant(flow.flowMeetingMeetingId, FlowNoteVariant.RAW)">
                            {{ 'viewer.book_meeting_panel.original' | transloco }}
                          </button>
                        </div>
                      }
                      <pre class="bmp__flow-note ph-mask">{{ activeNoteText(flow.flowMeetingMeetingId, detail) }}</pre>
                    </section>
                  }

                  <!-- Card footer row — Add to project on the left,
                       Open in Flow on the right. The Add to project
                       trigger always renders for any meeting in the
                       viewer's catalogue (project tagging is the
                       viewer's own action, not a meeting edit, so
                       canEdit is intentionally NOT gated here). -->
                  <div class="bmp__flow-card-footer">
                    @if (service.loadedCalendarMeetingId()) {
                      @let pickerOpen = isFlowCardPickerOpen(flow.flowMeetingMeetingId);
                      <div class="bmp__flow-card-project">
                        <button
                          type="button"
                          class="bmp__flow-card-project-trigger"
                          [class.bmp__flow-card-project-trigger--open]="pickerOpen"
                          [attr.aria-expanded]="pickerOpen"
                          (click)="pickerOpen ? closeFlowCardPicker() : openFlowCardPicker(flow.flowMeetingMeetingId)">
                          <paper-icon name="layers" [size]="11" />
                          {{ 'viewer.book_meeting_panel.add_to_project' | transloco }}
                          <paper-icon
                            [name]="pickerOpen ? 'chevron-up' : 'chevron-down'"
                            [size]="10" />
                        </button>

                        @if (pickerOpen) {
                          <div
                            class="bmp__flow-card-project-popover"
                            data-testid="flow-card-project-picker"
                            (keydown.escape)="closeFlowCardPicker()">
                            @if (flowCardJustAddedId()) {
                              <!-- Post-add success affordance — replaces
                                   the search + list briefly so the user
                                   sees confirmation before the dropdown
                                   auto-closes. -->
                              <div
                                class="bmp__flow-card-project-success"
                                role="status"
                                aria-live="polite">
                                <span class="bmp__flow-card-project-success-tick">
                                  <paper-icon name="check" [size]="12" />
                                </span>
                                <span class="bmp__flow-card-project-success-text ph-mask">
                                  {{ 'viewer.book_meeting_panel.added_to' | transloco }}
                                  <strong>{{ flowCardJustAddedName() }}</strong>
                                </span>
                              </div>
                            } @else if (flowCardPickerCreating()) {
                              <!-- Inline create form. Press Enter to
                                   create + attach in one shot. -->
                              <div class="bmp__flow-card-project-create-row">
                                <paper-icon name="plus" [size]="11" />
                                <input
                                  type="text"
                                  class="bmp__flow-card-project-search"
                                  [placeholder]="'viewer.book_meeting_panel.project_name_2' | transloco"
                                  autofocus
                                  [value]="flowCardPickerNewName()"
                                  (input)="flowCardPickerNewName.set($any($event.target).value)"
                                  (keydown.enter)="commitFlowCardCreate()"
                                  (keydown.escape)="cancelFlowCardCreate()"
                                  maxlength="96" />
                              </div>
                              <div class="bmp__flow-card-project-actions">
                                <paper-button
                                  class="bmp__flow-card-project-cancel"
                                  intent="ghost" size="xs"
                                  (click)="cancelFlowCardCreate()">
                                  {{ 'viewer.book_meeting_panel.cancel_4' | transloco }}
                                </paper-button>
                                <paper-button
                                  class="bmp__flow-card-project-save"
                                  intent="primary" size="xs"
                                  [disabled]="!flowCardPickerNewName().trim()"
                                  (click)="commitFlowCardCreate()">
                                  {{ 'viewer.book_meeting_panel.create_attach_2' | transloco }}
                                </paper-button>
                              </div>
                            } @else {
                              @if (flowCardPickerHasAnyProjects()) {
                                <input
                                  type="text"
                                  class="bmp__flow-card-project-search"
                                  [placeholder]="'viewer.book_meeting_panel.search_projects_2' | transloco"
                                  autofocus
                                  [value]="flowCardPickerQuery()"
                                  (input)="setFlowCardPickerQuery($any($event.target).value)" />

                                @let candidates = flowCardPickerSuggestions();
                                @if (candidates.length > 0) {
                                  <ul class="bmp__flow-card-project-list ph-mask" role="listbox">
                                    @for (project of candidates; track project._id) {
                                      <li>
                                        <button
                                          type="button"
                                          class="bmp__flow-card-project-option"
                                          (click)="commitFlowCardPickerAdd(project._id)">
                                          @if (project.icon) {
                                            <span class="bmp__flow-card-project-option-icon">
                                              {{ project.icon }}
                                            </span>
                                          } @else {
                                            <span
                                              class="bmp__flow-card-project-option-dot"
                                              [style.background]="project.colour || null"></span>
                                          }
                                          <span class="bmp__flow-card-project-option-text">
                                            <span class="bmp__flow-card-project-option-name">
                                              {{ project.name }}
                                            </span>
                                            <span class="bmp__flow-card-project-option-meta">
                                              {{ project.meetingCount }}
                                              {{ project.meetingCount === 1 ? 'meeting' : 'meetings' }}
                                            </span>
                                          </span>
                                        </button>
                                      </li>
                                    }
                                  </ul>
                                } @else {
                                  <p class="bmp__flow-card-project-empty">
                                    @if (flowCardPickerQuery()) {
                                      {{ 'viewer.book_meeting_panel.no_matching_projects' | transloco }}
                                    } @else {
                                      {{ 'viewer.book_meeting_panel.already_in_every_project' | transloco }}
                                    }
                                  </p>
                                }
                              } @else {
                                <p class="bmp__flow-card-project-empty">
                                  {{ 'viewer.book_meeting_panel.you_don_t_have_any_projects_2' | transloco }}
                                </p>
                              }

                              <button
                                type="button"
                                class="bmp__flow-card-project-manage"
                                (click)="beginFlowCardCreate()">
                                <paper-icon name="plus" [size]="11" />
                                @if (flowCardPickerHasAnyProjects()) {
                                  {{ 'viewer.book_meeting_panel.create_new_project_2' | transloco }}
                                  @if (flowCardPickerQuery()) {
                                    <span class="bmp__flow-card-project-manage-hint ph-mask">
                                      {{ 'viewer.book_meeting_panel.flowcardpickerquery' | transloco:{ flowCardPickerQuery: flowCardPickerQuery() } }}
                                    </span>
                                  }
                                } @else {
                                  {{ 'viewer.book_meeting_panel.create_your_first_project' | transloco }}
                                }
                              </button>
                            }
                          </div>
                        }
                      </div>
                    }

                    <!-- "Add to Workspace" chip is hidden for now —
                         "Add to project" is the canonical organising
                         action. When a project has a linked Knowledge
                         workspace, recordings auto-route there via
                         the backend's project-aware findOrCreate
                         FlowSessionsFolder. Re-enable by flipping
                         `addToWorkspaceChipEnabled` in the component
                         (no template change needed). -->
                    @if (addToWorkspaceChipEnabled() && service.loadedCalendarMeetingId()) {
                      @let wsPickerOpen = isFlowWorkspacePickerOpen(flow.flowMeetingMeetingId);
                      @let autoTarget = linkedWorkspaceForRouting();
                      <div class="bmp__flow-card-workspace">
                        @if (autoTarget && !wsPickerOpen) {
                          <!-- Auto-route — meeting is in a project with
                               a linked workspace, so we skip the picker
                               and the chip flips to "Save to <Name>". -->
                          <button
                            type="button"
                            class="bmp__flow-card-workspace-trigger bmp__flow-card-workspace-trigger--linked ph-block"
                            [title]="'Save this recording to ' + autoTarget.workspaceName + ' (linked via project)'"
                            (click)="commitFlowWorkspaceAutoRoute(flow.flowMeetingMeetingId)">
                            <paper-icon name="layers" [size]="11" />
                            {{ 'viewer.book_meeting_panel.save_to_autotarget' | transloco:{ autoTarget: autoTarget.workspaceName } }}
                          </button>
                        } @else {
                          <button
                            type="button"
                            class="bmp__flow-card-workspace-trigger"
                            [class.bmp__flow-card-workspace-trigger--open]="wsPickerOpen"
                            [attr.aria-expanded]="wsPickerOpen"
                            (click)="wsPickerOpen ? closeFlowWorkspacePicker() : openFlowWorkspacePicker(flow.flowMeetingMeetingId)">
                            <paper-icon name="book-open" [size]="11" />
                            {{ 'viewer.book_meeting_panel.add_to_workspace' | transloco }}
                            <paper-icon
                              [name]="wsPickerOpen ? 'chevron-up' : 'chevron-down'"
                              [size]="10" />
                          </button>
                        }

                        @if (wsPickerOpen) {
                          <div
                            class="bmp__flow-card-workspace-popover"
                            data-testid="flow-card-workspace-picker"
                            (keydown.escape)="closeFlowWorkspacePicker()">
                            @if (flowWorkspaceJustAddedId()) {
                              <div
                                class="bmp__flow-card-workspace-success"
                                role="status"
                                aria-live="polite">
                                <span class="bmp__flow-card-workspace-success-tick">
                                  <paper-icon name="check" [size]="12" />
                                </span>
                                <span class="bmp__flow-card-workspace-success-text ph-mask">
                                  {{ 'viewer.book_meeting_panel.added_to_2' | transloco }}
                                  <strong>{{ flowWorkspaceJustAddedName() }}</strong>
                                </span>
                              </div>
                            } @else {
                              @let workspaces = flowWorkspacePickerSuggestions();
                              @if (workspaces.length > 0) {
                                <ul class="bmp__flow-card-workspace-list ph-mask" role="listbox">
                                  @for (ws of workspaces; track ws._id) {
                                    <li>
                                      <button
                                        type="button"
                                        class="bmp__flow-card-workspace-option"
                                        (click)="commitFlowWorkspaceAdd(ws._id)">
                                        @if (ws.icon) {
                                          <span class="bmp__flow-card-workspace-option-icon">
                                            {{ ws.icon }}
                                          </span>
                                        } @else {
                                          <span class="bmp__flow-card-workspace-option-dot"></span>
                                        }
                                        <span class="bmp__flow-card-workspace-option-name">
                                          {{ ws.name }}
                                        </span>
                                      </button>
                                    </li>
                                  }
                                </ul>
                              } @else {
                                <p class="bmp__flow-card-workspace-empty">
                                  {{ 'viewer.book_meeting_panel.no_workspaces_available' | transloco }}
                                </p>
                              }
                            }
                          </div>
                        }
                      </div>
                    }

                    <a class="bmp__flow-card-open" [href]="flowOpenUrl(flow)">
                      <paper-icon name="external-link" [size]="14" /> {{ 'viewer.book_meeting_panel.open_in_flow' | transloco }}
                    </a>
                  </div>
                </article>
              }
            </section>
          }

          @if (hasJoinDetails()) {
            <section class="bmp__join-card" data-testid="join-card">
              <div class="bmp__section-label">
                <paper-icon name="video" [size]="12" />
                <span>{{ 'viewer.book_meeting_panel.join_meeting' | transloco }}</span>
              </div>
              @if (effectiveJoinUrl(); as url) {
                <a
                  class="bmp__join-cta"
                  [href]="url"
                  target="_blank"
                  rel="noopener"
                  data-testid="join-call-btn">
                  <paper-icon name="video" [size]="14" />
                  {{ 'viewer.book_meeting_panel.join_video_call' | transloco }}
                  <paper-icon name="arrow-right" [size]="14" />
                </a>
              }
              @if (parsedBody().meetingId; as id) {
                <button
                  type="button"
                  class="bmp__join-row bmp__join-row--copyable"
                  [attr.aria-label]="meetingIdCopied() ? 'Meeting ID copied' : 'Copy Meeting ID ' + id"
                  [title]="meetingIdCopied() ? 'Copied!' : 'Click to copy'"
                  (click)="onCopyMeetingId(id)">
                  <span class="bmp__join-row-label">{{ 'viewer.book_meeting_panel.meeting_id' | transloco }}</span>
                  <span class="bmp__join-row-value">
                    {{ id }}
                    <paper-icon [name]="meetingIdCopied() ? 'check' : 'copy'" [size]="11" />
                  </span>
                </button>
              }
              @if (parsedBody().passcode; as code) {
                <button
                  type="button"
                  class="bmp__join-row bmp__join-row--copyable"
                  [attr.aria-label]="passcodeCopied() ? 'Passcode copied' : 'Copy passcode'"
                  [title]="passcodeCopied() ? 'Copied!' : 'Click to copy'"
                  (click)="onCopyPasscode(code)">
                  <span class="bmp__join-row-label">{{ 'viewer.book_meeting_panel.passcode' | transloco }}</span>
                  <span class="bmp__join-row-value">
                    {{ code }}
                    <paper-icon [name]="passcodeCopied() ? 'check' : 'copy'" [size]="11" />
                  </span>
                </button>
              }
              @if (parsedBody().helpUrl || parsedBody().organiserOptionsUrl) {
                <div class="bmp__join-links">
                  @if (parsedBody().helpUrl; as help) {
                    <a [href]="help" target="_blank" rel="noopener">{{ 'viewer.book_meeting_panel.help' | transloco }}</a>
                  }
                  @if (parsedBody().organiserOptionsUrl; as opts) {
                    <a [href]="opts" target="_blank" rel="noopener">{{ 'viewer.book_meeting_panel.meeting_options' | transloco }}</a>
                  }
                </div>
              }
            </section>
          }
        } @else {
          <!-- Meeting section — title + date/time editing surfaces +
               AI subject chip + Smart Assist chip. Anchored with
               id="bmp-sec-meeting" so the sticky tab bar's click +
               scroll-spy can target it. In the new scroll-to-tab
               layout every section renders inline; the tab bar drives
               the smooth-scroll, and the visible tab updates as the
               user scrolls past each anchor. -->
          <section id="bmp-sec-meeting" class="bmp__tab-section"
                   role="tabpanel"
                   aria-labelledby="bmp-tab-meeting">
            <im-meeting-details-section />

            <!-- Schedule V2 — category + tags + Flow project inline
                 strip. Extracted into <bmp-classify-row> so the same
                 cluster renders in view-mode too (writes route via
                 the viewer-overlay save methods in that mode). -->
            @if (scheduleV2Enabled) {
              <div class="bmp__section bmp__section--spaced">
                <bmp-classify-row
                  [categoryId]="service.meetingCategoryId()"
                  [tags]="service.meetingTags()"
                  [flowProjectId]="service.flowProjectId()"
                  [flowProjects]="flowProjects.activeProjects()"
                  mode="create"
                  (categoryChanged)="onCategoryPicked($event)"
                  (tagsChanged)="onTagsChanged($event)"
                  (flowProjectChanged)="onClassifyFlowChanged($event)"
                  (newProjectRequested)="onClassifyNewProject($event)"
                  (flowPickerOpened)="onClassifyFlowPickerOpened()" />
              </div>
            }

            <!-- AI subject chip — one-tap-to-accept suggestion. Renders
                 only when the user has added attendees + the title is
                 blank + the backend produced a non-null suggestion. The
                 whole flow is silent on failure (`aiSubjectSuggestion`
                 stays null → chip hidden) so a misconfigured GenAI
                 environment never blocks the form. -->
            @if (service.aiSubjectSuggestion(); as suggested) {
              @if (!service.subject().trim()) {
                <div class="bmp__section bmp__section--spaced">
                  <div class="bmp__ai-subject-chip" role="region" [attr.aria-label]="'viewer.book_meeting_panel.suggested_subject' | transloco">
                    <span class="bmp__ai-subject-icon" aria-hidden="true">
                      <paper-icon name="ai-spark" [size]="12" />
                    </span>
                    <span class="bmp__ai-subject-label">{{ 'viewer.book_meeting_panel.suggested' | transloco }}</span>
                    <paper-button
                      class="bmp__ai-subject-accept"
                      intent="ai" size="xs"
                      data-testid="ai-subject-accept"
                      (click)="service.acceptSubjectSuggestion()">
                      {{ suggested }}
                    </paper-button>
                    <paper-button
                      class="bmp__ai-subject-dismiss"
                      intent="ghost" shape="icon" size="xs"
                      icon="x" [iconSize]="10"
                      [ariaLabel]="'viewer.book_meeting_panel.dismiss_suggestion' | transloco"
                      (click)="service.dismissSubjectSuggestion()" />
                  </div>
                </div>
              }
            }

            <!-- Schedule V2 — wrap date-time in the icon-gutter row
                 pattern (clock icon + "WHEN" label + date pill) so it
                 lines up vertically with Rooms / People below. -->
            @if (scheduleV2Enabled) {
              <!-- Prototype's `.bp-row` pattern: icon gutter on the left,
                   label + single pill on the right. The pill is the
                   date-time-section's own .dts__summary; NO card wrapper
                   (that would be card-in-card per the prototype). -->
              <div class="bmp__section bmp__section--spaced bmp__section--v2-gutter">
                <span class="bmp__section-icon" aria-hidden="true">
                  <paper-icon name="clock" [size]="14" />
                </span>
                <div class="bmp__section-title">
                  <span>{{ 'viewer.book_meeting_panel.when' | transloco }}</span>
                </div>
                <im-date-time-section />
              </div>
            } @else {
              <im-date-time-section />
            }

            <!-- Smart Assist chip — classifies the meeting from subject +
                 attendees + duration and offers one-click actions. Same
                 atom the scheduling-dialog uses, so the suggestions stay
                 consistent across both surfaces. Renders only when the
                 classifier hits a known intent above the confidence floor;
                 it dismisses itself for the rest of the session via
                 sessionStorage. -->
            @if (meetingIntent(); as intent) {
              <div class="bmp__section bmp__section--spaced">
                <sd-intent-hint-chip
                  [intentType]="intent.type"
                  [confidence]="intent.confidence"
                  [currentDuration]="service.duration() ?? 30"
                  [extractedNames]="[]"
                  (durationSuggested)="onIntentDurationSuggested($event)"
                  (spaceTypeApplied)="onIntentSpaceTypeApplied($event)"
                  (nameSearchRequested)="onIntentNameSearchRequested($event)" />
              </div>
            }

            <!-- Note: the AI side rail used to render inline here. It's
                 now pinned in the right column of the V2 two-column
                 body — see <bmp-ai-side> below the closing main pane. -->
          </section>
        }

        <!-- IM-KEEP: building alerts overlapping the meeting window. -->
        @if (meetingAlerts().length > 0) {
          <div class="bmp__section bmp__section--spaced bmp__section--v2-gutter">
            <paper-alerts-banner [alerts]="meetingAlerts()" context="meeting" />
          </div>
        }

        <!-- Rooms section anchor — scroll-spy target id="bmp-sec-rooms". -->
        <section id="bmp-sec-rooms" class="bmp__tab-section"
                 role="tabpanel"
                 aria-labelledby="bmp-tab-rooms">
        <div class="bmp__section bmp__section--spaced bmp__section--v2-gutter">
          <span class="bmp__section-icon" aria-hidden="true">
            <paper-icon name="door" [size]="14" />
          </span>
          <div class="bmp__section-title">
            <span>{{ 'viewer.book_meeting_panel.rooms' | transloco }}</span>
            <span class="bmp__section-count">{{ service.rooms().length }}</span>
            <div class="bmp__section-right"></div>
          </div>
          @for (room of service.rooms(); track room.spaceId) {
            @let avail = roomAvailability(room.spaceId);
            <div
              class="bmp__room-card"
              [class.bmp__room-card--primary]="room.isPrimary"
              [class.bmp__room-card--unavailable]="avail.status === 'busy'"
              [class.primary]="room.isPrimary"
              [class.unavailable]="avail.status === 'busy'"
              role="group"
              [attr.aria-labelledby]="'bmp-room-name-' + room.spaceId"
              [attr.data-space-id]="room.spaceId"
              [attr.data-availability]="avail.status">

              <!-- Flat header — thumbnail + name block + Change action.
                   Mirrors the prototype `.bp-room-card-head` (8px padding,
                   10px gap, 44×44 thumb). Inline pills (PRIMARY / AVAILABLE
                   / BUSY / OVERFLOW) live next to the name so the row reads
                   as one identity cluster. -->
              <div class="bmp__room-header">
                <div class="bmp__room-thumb bmp__room-photo"
                  role="img" [attr.aria-label]="'Photo of ' + room.name"
                  [class.bmp__room-photo--empty]="!room.image"
                  [style.background-image]="room.image ? 'url(' + room.image + ')' : null">
                  @if (!room.image) {
                    <span class="bmp__room-photo-monogram" aria-hidden="true">
                      {{ (room.name || '?').trim().charAt(0).toUpperCase() }}
                    </span>
                  }
                  @if (!scheduleV2Enabled) {
                    <span class="bmp__room-city-badge">{{ (room.locationName || 'BHM').split(' ')[0] }}</span>
                  }
                </div>
                <div class="bmp__room-name-block bmp__room-info">
                  <div class="bmp__room-name-row">
                    <span class="bmp__room-name" [id]="'bmp-room-name-' + room.spaceId">{{ room.name }}</span>
                    @if (room.isPrimary) {
                      <span class="bmp__room-pill bmp__room-pill--primary">{{ 'viewer.book_meeting_panel.primary' | transloco }}</span>
                    }
                    @if (avail.status === 'available') {
                      <span class="bmp__room-pill bmp__room-pill--available">{{ 'viewer.book_meeting_panel.available' | transloco }}</span>
                    } @else if (avail.status === 'busy') {
                      <!-- Design contract (flow-form): unavailable rooms carry a
                           red "Conflict" chip. The availability signal itself is
                           unchanged — only the chip copy/tone is design-aligned. -->
                      <span class="bmp__room-pill bmp__room-pill--busy" [title]="avail.label">{{ 'viewer.book_meeting_panel.conflict' | transloco }}</span>
                    }
                    @if (room.roomRole === 'overflow') {
                      <span class="bmp__room-pill bmp__room-pill--overflow">{{ 'viewer.book_meeting_panel.overflow' | transloco }}</span>
                    }
                  </div>
                  <div class="bmp__room-meta">
                    @let metaParts = roomMetaParts(room);
                    @if (metaParts.length > 0) {
                      @for (part of metaParts; track $index) {
                        @if ($index > 0) {
                          <span aria-hidden="true">·</span>
                        }
                        @if (part === '__capacity__') {
                          <span class="bmp__room-capacity"
                            [class.bmp__room-capacity--adjusted]="isCapacityAdjusted(room)"
                            [title]="capacityTooltip(room)">
                            <paper-icon name="users" [size]="11" />
                            {{ 'viewer.book_meeting_panel.seats_capacitylabel' | transloco:{ capacityLabel: capacityLabel(room) } }}
                          </span>
                        } @else {
                          <span>{{ part }}</span>
                        }
                      }
                    } @else {
                      <span>{{ 'viewer.book_meeting_panel.meeting_room' | transloco }}</span>
                    }
                    @if (roomRating(room); as rating) {
                      <span aria-hidden="true">·</span>
                      <span class="bmp__room-rating"
                        cdkOverlayOrigin
                        #ratingOrigin="cdkOverlayOrigin"
                        tabindex="0"
                        [attr.aria-label]="'Rating ' + rating + '. Hover to see breakdown.'"
                        (mouseenter)="onShowTooltip(room.spaceId, 'rating')"
                        (mouseleave)="onHideTooltip()"
                        (focus)="onShowTooltip(room.spaceId, 'rating')"
                        (blur)="onHideTooltip()">
                        <paper-icon name="star" [size]="12" aria-hidden="true" />
                        {{ rating | number:'1.1-1' }}
                      </span>
                      <ng-template
                        cdkConnectedOverlay
                        [cdkConnectedOverlayOrigin]="ratingOrigin"
                        [cdkConnectedOverlayOpen]="isTooltipOpen(room.spaceId, 'rating')"
                        [cdkConnectedOverlayPositions]="tooltipPositions">
                        <div class="bmp__room-tooltip" role="tooltip">
                          <div class="bmp__room-tooltip-head">
                            <paper-icon name="star" [size]="13" />
                            <span>{{ rating | number:'1.1-1' }} from {{ room.rating?.totalRatings }} {{ room.rating?.totalRatings === 1 ? 'review' : 'reviews' }}</span>
                          </div>
                          @if (room.rating?.breakdown?.length) {
                            <ul class="bmp__room-tooltip-bars" role="list">
                              @for (b of room.rating?.breakdown; track b.stars) {
                                <li class="bmp__room-tooltip-bar-row">
                                  <span class="bmp__room-tooltip-bar-label">{{ b.stars }}★</span>
                                  <span class="bmp__room-tooltip-bar-track" aria-hidden="true">
                                    <span class="bmp__room-tooltip-bar-fill"
                                      [style.width.%]="(room.rating?.totalRatings ? (b.count / room.rating!.totalRatings) * 100 : 0)"></span>
                                  </span>
                                  <span class="bmp__room-tooltip-bar-count">{{ b.count }}</span>
                                </li>
                              }
                            </ul>
                          } @else {
                            <p class="bmp__room-tooltip-empty">{{ 'viewer.book_meeting_panel.no_detailed_breakdown_yet' | transloco }}</p>
                          }
                        </div>
                      </ng-template>
                    }
                    @if (roomOpenIssues(room) > 0) {
                      <span aria-hidden="true">·</span>
                      <span class="bmp__room-issues"
                        cdkOverlayOrigin
                        #issuesOrigin="cdkOverlayOrigin"
                        tabindex="0"
                        [attr.aria-label]="roomOpenIssues(room) + ' open ' + (roomOpenIssues(room) === 1 ? 'issue' : 'issues') + '. Hover for details.'"
                        (mouseenter)="onShowTooltip(room.spaceId, 'issues')"
                        (mouseleave)="onHideTooltip()"
                        (focus)="onShowTooltip(room.spaceId, 'issues')"
                        (blur)="onHideTooltip()">
                        <paper-icon name="alert-triangle" [size]="11" aria-hidden="true" />
                        {{ roomOpenIssues(room) }}
                      </span>
                      <ng-template
                        cdkConnectedOverlay
                        [cdkConnectedOverlayOrigin]="issuesOrigin"
                        [cdkConnectedOverlayOpen]="isTooltipOpen(room.spaceId, 'issues')"
                        [cdkConnectedOverlayPositions]="tooltipPositions">
                        <div class="bmp__room-tooltip" role="tooltip">
                          <div class="bmp__room-tooltip-head">
                            <paper-icon name="alert-triangle" [size]="13" />
                            <span>{{ roomOpenIssues(room) }} open {{ roomOpenIssues(room) === 1 ? 'issue' : 'issues' }} on this room</span>
                          </div>
                          <ul class="bmp__room-tooltip-issues" role="list">
                            @for (i of (room.issues?.items ?? []); track i.id) {
                              <li class="bmp__room-tooltip-issue">
                                <span class="bmp__room-tooltip-issue-text">{{ i.text || 'Issue' }}</span>
                                @if (i.priority) {
                                  <span class="bmp__room-tooltip-issue-priority"
                                    [attr.data-priority]="i.priority">{{ i.priority }}</span>
                                }
                              </li>
                            }
                          </ul>
                          <p class="bmp__room-tooltip-foot">{{ 'viewer.book_meeting_panel.may_affect_your_booking_check_the' | transloco }}</p>
                        </div>
                      </ng-template>
                    }
                  </div>
                </div>
                @if (scheduleV2Enabled && service.mode() !== 'view') {
                  <paper-button
                    intent="ghost" size="sm"
                    class="bmp__room-change"
                    [ariaLabel]="'Change room ' + room.name"
                    (click)="onChangeRoom(room.spaceId)">{{ 'viewer.book_meeting_panel.change' | transloco }}</paper-button>
                }
                @if (!room.isPrimary && service.mode() !== 'view') {
                  <paper-button
                    class="bmp__room-remove"
                    intent="ghost" shape="icon" size="xs"
                    icon="close" [iconSize]="12"
                    [ariaLabel]="'viewer.book_meeting_panel.remove_room' | transloco"
                    [data-testid]="'remove-room-' + room.spaceId"
                    (click)="onRemoveRoom(room.spaceId)" />
                }
              </div>

              <!-- Dashed divider + control row — Layout picker + Role
                   select + dashed "+ Add catering" pill. The dashed
                   border-top on .bmp__room-controls is the prototype's
                   visual separator between identity (above) and the
                   actionable controls (below). -->
              @if (scheduleV2Enabled && service.mode() !== 'view') {
                <div class="bmp__room-controls">
                  <div class="bmp__room-control">
                    <span class="bmp__room-control-label" [attr.id]="'bmp-room-layout-label-' + room.spaceId">{{ 'viewer.book_meeting_panel.layout' | transloco }}</span>
                    <bmp-layout-picker
                      [value]="resolveLayoutKey(room.layoutKey)"
                      [roomCapacity]="room.capacity ?? 0"
                      [spaceLayouts]="room.layouts"
                      (layoutPicked)="onLayoutPicked(room.spaceId, $event)" />
                  </div>
                  <div class="bmp__room-control">
                    <span class="bmp__room-control-label" [attr.id]="'bmp-room-role-label-' + room.spaceId">{{ 'viewer.book_meeting_panel.role' | transloco }}</span>
                    <select
                      class="bmp__room-role-select"
                      [attr.aria-labelledby]="'bmp-room-role-label-' + room.spaceId"
                      [value]="roomRole(room)"
                      (change)="onRoleChange(room.spaceId, $any($event.target).value)">
                      <option value="primary" [disabled]="!room.isPrimary">{{ 'viewer.book_meeting_panel.primary' | transloco }}</option>
                      <option value="overflow">{{ 'viewer.book_meeting_panel.overflow' | transloco }}</option>
                      <option value="satellite">{{ 'viewer.book_meeting_panel.satellite' | transloco }}</option>
                    </select>
                  </div>
                  <paper-button
                    *imFeatureFlag="'catering'"
                    [id]="'bmp-room-add-catering-' + room.spaceId"
                    class="bmp__room-add-catering"
                    intent="secondary" size="xs"
                    icon="plus" [iconSize]="11"
                    data-testid="add-catering-btn"
                    [attr.data-space-id]="room.spaceId"
                    [ariaLabel]="'Add catering to ' + room.name"
                    (click)="openCateringDialog(room)">
                    {{ 'viewer.book_meeting_panel.add_catering_2' | transloco }}
                  </paper-button>
                </div>
                <!-- Setup / reset hint — inline replacement for the
                     warning that previously lived inside im-room-services.
                     Renders only when the active layout actually
                     reserves setup or reset time on the room schedule. -->
                @if (roomLayoutSetupHint(room); as hint) {
                  <div class="bmp__room-setup-hint" role="status">
                    <paper-icon name="clock" [size]="11" aria-hidden="true" />
                    <span>{{ hint }}</span>
                  </div>
                }
              }
              <!-- Day tabs for multi-day meetings -->
              @if (service.isMultiDay() && service.meetingDays().length > 1) {
                <div class="bmp__day-tabs">
                  @for (day of service.meetingDays(); track day; let i = $index) {
                    <button class="bmp__day-tab"
                      [class.active]="activeDayForRoom(room.spaceId) === i"
                      (click)="setActiveDayForRoom(room.spaceId, i)">
                      {{ 'viewer.book_meeting_panel.day_i' | transloco:{ i: i + 1 } }}
                      @if (service.ordersForRoomAndDay(room.spaceId, i).length > 0) {
                        <span class="bmp__day-tab-dot"></span>
                      }
                    </button>
                  }
                </div>
              }

              <!-- Copy catering from another day (multi-day only) -->
              @if (service.isMultiDay() && service.meetingDays().length > 1) {
                @let activeDay = activeDayForRoom(room.spaceId);
                @let currentOrders = service.ordersForRoomAndDay(room.spaceId, activeDay);
                @let sourceDay = sourceDayForCopy(room.spaceId, activeDay);

                @if (currentOrders.length === 0 && sourceDay !== null) {
                  <paper-button class="bmp__copy-day-btn" intent="ghost" size="xs"
                    icon="copy" [iconSize]="11"
                    (click)="service.copyCateringFromDay(room.spaceId, sourceDay, activeDay)">
                    {{ 'viewer.book_meeting_panel.copy_from_day_sourceday' | transloco:{ sourceDay: sourceDay + 1 } }}
                  </paper-button>
                }

                @if (currentOrders.length > 0) {
                  <paper-button class="bmp__copy-day-btn" intent="ghost" size="xs"
                    icon="copy-check" [iconSize]="11"
                    (click)="service.copyCateringToAllDays(room.spaceId, activeDay)">
                    {{ 'viewer.book_meeting_panel.apply_to_all_days' | transloco }}
                  </paper-button>
                }
              }

              <!-- Room services (layout, equipment, AV).
                   Schedule V2 — hide this block entirely. The V2
                   layout picker above already owns the Layout choice
                   and shows setup/reset minutes in its popover; the
                   bottom tile was a leftover from the legacy services
                   panel and caused the duplicate "Boardroom" tile
                   the user reported. Setup warning surfaces as an
                   inline hint right next to the picker (see
                   layoutSetupHint render). -->
              @if (!scheduleV2Enabled) {
                <im-room-services
                  [roomId]="room.spaceId"
                  [services]="roomServices(room.spaceId)"
                  [hasRemote]="hasRemoteAttendees()"
                  [hasExternal]="hasExternalAttendees()"
                  [roomCapacity]="room.capacity || 0"
                  [spaceLayouts]="room.layouts || []"
                  [spaceEquipment]="room.equipmentConfig"
                  (servicesChange)="onRoomServicesChange(room.spaceId, $event)" />
              }

              <!-- Service requests — filtered by day when multi-day -->
              @for (entry of currentServicesForRoom(room.spaceId); track $index) {
                @switch (entry.kind) {
                  @case ('catering') {
                    <ct-service-card
                      [draft]="entry.data"
                      (edit)="onEditCatering(entry.data, room)"
                      (remove)="service.removeCateringOrder(entry.data.idempotencyKey)" />
                  }
                  @default {
                    <div class="bmp__service-stub">{{ 'viewer.book_meeting_panel.entry_coming_soon' | transloco:{ entry: entry.kind } }}</div>
                  }
                }
              }
              @if (!scheduleV2Enabled && service.mode() !== 'view') {
                <paper-button
                  *imFeatureFlag="'catering'"
                  [id]="'bmp-add-catering-' + room.spaceId"
                  class="bmp__add-catering-btn"
                  intent="secondary" size="sm"
                  icon="plus" [iconSize]="13"
                  [attr.data-space-id]="room.spaceId"
                  (click)="service.openCateringForRoom(room, activeDayForRoom(room.spaceId))">
                  {{ 'viewer.book_meeting_panel.add_catering_3' | transloco }}
                </paper-button>
              }
            </div>
          }
          @if (service.rooms().length === 0) {
            @if (service.mode() === 'view') {
              <p class="bmp__empty-hint">{{ 'viewer.book_meeting_panel.room_not_specified' | transloco }}</p>
            } @else {
              <p class="bmp__empty-hint">{{ 'viewer.book_meeting_panel.select_a_room_from_the_schedule' | transloco }}</p>
            }
          }

          @if (service.mode() !== 'view') {
            <!-- Collapsed by default — click "+ Add room" to expand
                 the typeahead inline. Matches the prototype which
                 puts the search behind an affordance instead of
                 always-on chrome (so the rooms list dominates the
                 tab). The picker emits (cancel) to collapse again. -->
            @if (showRoomPicker()) {
              <im-room-picker-inline (cancel)="showRoomPicker.set(false)" />
            } @else {
              <paper-button
                class="bmp__add-room-btn"
                intent="secondary" size="sm"
                icon="plus" [iconSize]="12"
                data-testid="bmp-add-room-btn"
                [ariaLabel]="'viewer.book_meeting_panel.add_a_room_to_this_meeting' | transloco"
                (click)="showRoomPicker.set(true)">
                {{ 'viewer.book_meeting_panel.add_room' | transloco }}
              </paper-button>
            }
          }
        </div>
        </section>

        <!-- People section anchor — scroll-spy target id="bmp-sec-people". -->
        <section id="bmp-sec-people" class="bmp__tab-section"
                 role="tabpanel"
                 aria-labelledby="bmp-tab-people">
        <div class="bmp__section bmp__section--spaced bmp__section--v2-gutter">
          <span class="bmp__section-icon" aria-hidden="true">
            <paper-icon name="people" [size]="14" />
          </span>
          <div class="bmp__section-title">
            <span>{{ 'viewer.book_meeting_panel.people' | transloco }}</span>
            <span class="bmp__section-count">{{ service.attendees().length }}</span>
            <div class="bmp__section-right"></div>
          </div>
          <!-- Attendee rows render flat on the cream canvas per the
               prototype's `.bp-attendees`; no card wrapper around the
               whole stack. -->
          <im-attendees-section />

        </div>
        </section>

        <!-- VISITORS — guests from outside who need reception to expect them.
             Create mode: added here, raised as visits linked to the booking
             once the room is booked. View mode: the visitors YOU invited to
             this meeting, with where they are. Mounted only behind the
             visitors flag, and in create mode only for a viewer who can
             raise a visit (visit:create). -->
        @if (showVisitors()) {
          <section id="bmp-sec-visitors" class="bmp__tab-section">
            <div class="bmp__section bmp__section--spaced bmp__section--v2-gutter">
              <span class="bmp__section-icon" aria-hidden="true">
                <paper-icon name="user-plus" [size]="14" />
              </span>
              <div class="bmp__section-title">
                <span>{{ 'visitors.meeting.title' | transloco }}</span>
                @if (!isView() && meetingVisitors.drafts().length) {
                  <span class="bmp__section-count">{{ meetingVisitors.drafts().length }}</span>
                }
                <div class="bmp__section-right"></div>
              </div>
              <im-bmp-visitors [viewMode]="isView()" />
            </div>
          </section>
        }

        <!-- AVAILABILITY section — find-time strip lifted out of the
             People block so it gets its own .bmp__section--v2-gutter
             shell with the clock icon in the gutter column. Reads
             clearly as "Availability for THIS meeting" via the strip's
             internal header. View-mode opens it default-collapsed so
             the panel scans as the meeting summary first; create-mode
             defaults to expanded since the user actively needs the
             find-time view while picking a slot. -->
        @if (scheduleV2Enabled && service.attendees().length > 0 && service.startTime()) {
          <section id="bmp-sec-availability" class="bmp__tab-section"
                   role="tabpanel"
                   aria-labelledby="bmp-tab-availability">
            <div class="bmp__section bmp__section--spaced bmp__section--v2-gutter">
              <span class="bmp__section-icon" aria-hidden="true">
                <paper-icon name="clock" [size]="14" />
              </span>
              <div class="bmp__section-title">
                <span>{{ 'viewer.book_meeting_panel.availability' | transloco }}</span>
                <span class="bmp__section-count">{{ findTimeAttendees().length }}</span>
                <div class="bmp__section-right"></div>
              </div>
              <bmp-find-time-strip
                [meetingStartMin]="footerStartMin()"
                [meetingEndMin]="footerStartMin() + (service.duration() ?? 30)"
                [attendees]="findTimeAttendees()"
                [betterTimes]="findTimeBetterTimes()"
                [mode]="isView() ? 'view' : 'create'"
                (timePicked)="onFindTimePicked($event)" />
            </div>
          </section>
        }

        <!-- Notes section anchor — viewer-private planning notes +
             AI brief. ALWAYS renders, in both create AND view modes.
             View-mode framing: people talk to the viewer about
             meetings they're attending (not organising), and they
             need somewhere to capture that context. The notes-section
             component takes a [viewMode] input that suppresses the
             public-agenda editor in view-mode (agenda renders as a
             read-only card elsewhere) while keeping the private
             notes editor intact. Notes round-trip via the existing
             saveMeetingNotes mutation; the public agenda is never
             edited from this surface in view-mode. -->
        <section id="bmp-sec-notes" class="bmp__tab-section"
                 role="tabpanel"
                 aria-labelledby="bmp-tab-notes">
          <div class="bmp__section bmp__section--spaced bmp__section--v2-gutter">
            <span class="bmp__section-icon" aria-hidden="true">
              <paper-icon name="shield" [size]="14" />
            </span>
            <div class="bmp__section-title">
              <span>{{ 'viewer.book_meeting_panel.your_notes' | transloco }}</span>
              @if (service.internalNotes().trim()) {
                <span class="bmp__section-count">{{ service.internalNotes().length }}</span>
              }
              <div class="bmp__section-right"></div>
            </div>
            <im-notes-section [viewMode]="isView()" />
          </div>
        </section>

        <!-- Linked catering orders (read-only) — view + edit modes only.
             The CREATE flow keeps its own draft picker on each room card.
             In view/edit we surface orders that already exist on the meeting
             so the user can see them at a glance; new orders mid-edit are
             out of scope for now. Hidden when there's nothing to show. -->
        @if (service.mode() !== 'create' && service.linkedCateringOrders().length > 0) {
          <div class="bmp__section bmp__section--linked-catering bmp__section--spaced">
            <div class="bmp__section-title">
              <span class="bmp__section-dot"></span>
              <span>{{ 'viewer.book_meeting_panel.catering' | transloco }}</span>
              <span class="bmp__catering-attached-chip">
                {{ service.linkedCateringOrders().length }} order{{ service.linkedCateringOrders().length === 1 ? '' : 's' }} attached
              </span>
              <div class="bmp__section-right"></div>
            </div>

            @for (group of groupedLinkedCateringOrders(); track $index) {
              @if (group.roomName) {
                <div class="bmp__catering-group-label">{{ group.roomName }}</div>
              }
              <ul class="bmp__linked-catering-list" role="list">
                @for (order of group.orders; track order._id) {
                  <li class="bmp__linked-catering-row" [attr.data-status]="order.status">
                    <div class="bmp__linked-catering-main">
                      <div class="bmp__linked-catering-vendor">
                        {{ order.vendorName }}
                      </div>
                      <div class="bmp__linked-catering-meta">
                        {{ order.itemCount }} item{{ order.itemCount === 1 ? '' : 's' }}
                        · {{ formatLinkedOrderTotal(order) }}
                      </div>
                      <div class="bmp__linked-catering-time">
                        {{ formatDeliveryTime(order.deliveryTime) }}
                      </div>
                      @if (order.cancelledReason) {
                        <div class="bmp__linked-catering-sub bmp__linked-catering-sub--danger">
                          {{ order.cancelledReason }}
                        </div>
                      } @else if (order.approval && order.approval.state === 'pending') {
                        <div class="bmp__linked-catering-sub">
                          Awaiting approval from {{ order.approval.approverName || 'an approver' }}
                        </div>
                      } @else if (order.vendorNotifiedAt) {
                        <div class="bmp__linked-catering-sub">
                          {{ 'viewer.book_meeting_panel.vendor_notified_at_formattimeonly' | transloco:{ formatTimeOnly: formatTimeOnly(order.vendorNotifiedAt) } }}
                        </div>
                      }
                    </div>
                    <div class="bmp__linked-catering-aside">
                      <span
                        class="bmp__linked-catering-status"
                        [attr.data-status]="order.status">
                        {{ order.status.replaceAll('_', ' ') }}
                      </span>
                      <span class="bmp__linked-catering-num">{{ 'viewer.book_meeting_panel.cat_order' | transloco:{ order: order.orderNumber } }}</span>
                    </div>
                  </li>
                }
              </ul>
            }
          </div>
        }

        @if (!isView()) {
          <!-- Options section anchor — scroll-spy target id="bmp-sec-options".
               Holds the Teams toggle + collapsible More Options. Conferencing
               only renders when the room syncs with a real calendar. -->
          <section id="bmp-sec-options" class="bmp__tab-section"
                   role="tabpanel"
                   aria-labelledby="bmp-tab-options">
            @if (service.isCalendarBacked()) {
              @if (scheduleV2Enabled) {
                <!-- Teams / video toggle — prototype `.bp-row` with video
                     icon gutter. Conferencing-section renders the toggle
                     directly in the right column. -->
                <div class="bmp__section bmp__section--spaced bmp__section--v2-gutter">
                  <span class="bmp__section-icon" aria-hidden="true">
                    <paper-icon name="video" [size]="14" />
                  </span>
                  <im-conferencing-section />
                </div>
              } @else {
                <im-conferencing-section />
              }
            }

            <!-- Flow integration is now a pill in the classify row at
                 the top of the form (next to Category + Tags). The old
                 dedicated Flow row used to live here. -->

            <im-more-options-section />
          </section>
        }

        <!-- AGENDA section — view-mode read-only. Lives in the
             .bmp__section--v2-gutter shell so the section-icon gutter
             aligns vertically with Rooms / People / Availability above.
             When the meeting has no body the card shows an explicit
             empty state so users know the section was checked, not
             silently hidden. -->
        @if (isView()) {
          <section id="bmp-sec-agenda" class="bmp__tab-section"
                   role="tabpanel"
                   aria-labelledby="bmp-tab-agenda">
            <div class="bmp__section bmp__section--spaced bmp__section--v2-gutter">
              <span class="bmp__section-icon" aria-hidden="true">
                <paper-icon name="message-square" [size]="14" />
              </span>
              <div class="bmp__section-title">
                <span>{{ 'viewer.book_meeting_panel.agenda' | transloco }}</span>
                <div class="bmp__section-right"></div>
              </div>
              <div class="bmp__agenda-card" data-testid="agenda-card">
                @if (hasAgenda()) {
                  <div class="bmp__agenda-body" [innerHTML]="agendaSafeHtml()"></div>
                } @else {
                  <p class="bmp__agenda-empty">{{ 'viewer.book_meeting_panel.no_agenda_or_notes_attached_to' | transloco }}</p>
                }
              </div>
            </div>
          </section>
        }
        </div><!-- /.bmp__v2-main -->

        <!-- Schedule V2 — right rail with the AI side panel. Pinned
             across every tab (Meeting / Rooms / People / Briefing /
             Options). Suppressed in view mode (the hero header +
             agenda card already fill the right space). -->
        <!-- The whole rail (Plan-with-AI chips, similar meetings,
             suggested rooms) is the meeting-prep AI surface, so it
             hides as one unit when the platform.meeting-prep-ai
             capability is off — the backend refuses the underlying
             queries in that state. -->
        @if (scheduleV2Enabled && !isView()) {
          <aside *imFeatureFlag="'platform.meeting-prep-ai'"
                 class="bmp__v2-rail" data-testid="bmp-ai-rail">
            <bmp-ai-side
              [title]="service.subject()"
              [attendees]="aiSideAttendeeEmails()"
              [attendeeNames]="aiSideAttendeeNames()"
              [durationMin]="service.duration() ?? 60"
              [notes]="aiAgendaNotes()"
              [flowProjectContext]="aiFlowProjectContext()"
              [hasCateringAlready]="service.hasCatering()"
              [suggestingTitle]="service.aiSubjectSuggestionLoading()"
              [titleSuggestion]="service.aiSubjectSuggestion()"
              [summarisingLast]="service.lastMeetingSummaryLoading()"
              [lastSummary]="service.lastMeetingSummary()"
              (agendaDrafted)="onAgendaDrafted($event)"
              (suggestTitleRequested)="onSuggestTitleRequested()"
              (acceptTitleSuggestion)="onAcceptTitleSuggestion()"
              (dismissTitleSuggestion)="onDismissTitleSuggestion()"
              (summariseLastRequested)="onSummariseLastRequested()"
              (useAsAgendaRequested)="onUseAsAgenda($event)"
              (dismissLastSummary)="onDismissLastSummary()"
              (similarPicked)="onSimilarPicked($event)"
              (roomPicked)="onSuggestedRoomPicked($event)" />
          </aside>
        }
        </div><!-- /.bmp__v2-body -->
      }

      <!-- Catering sub-views — gated on the `catering` catalogue flag.
           When a customer disables catering the views collapse and the
           parent BookMeetingService never reaches these states (the
           gated "Add catering" buttons above never fire). The gate
           here is defence-in-depth in case the service is triggered
           programmatically (e.g. resumed from a draft). -->
      @case (BookMeetingView.VENDOR_BROWSE) {
        <ng-container *imFeatureFlag="'catering'">
          <im-catering-vendor-list />
        </ng-container>
      }

      @case (BookMeetingView.MENU_BROWSE) {
        <ng-container *imFeatureFlag="'catering'">
          <ct-menu-browse />
        </ng-container>
      }

      @case (BookMeetingView.CART_REVIEW) {
        <ng-container *imFeatureFlag="'catering'">
          <ct-cart-summary />
        </ng-container>
      }

      @case (BookMeetingView.EMAIL_REQUEST) {
        <ng-container *imFeatureFlag="'catering'">
          <ct-email-request-form />
        </ng-container>
      }

      @case (BookMeetingView.CONFIRMATION) {
        <im-confirmation-section />
        <im-bmp-visitors-outcome />
      }
    }
  </div>

  <!-- Footer — mode-aware.
       Create  → Cancel + Book Meeting / Send for Approval (existing behaviour)
       View    → Edit (when canEdit) + Close, with Join Video Call when the
                 meeting is online + future + viewer can't edit.
       Edit    → Save changes + Discard (Cancel meeting still gated on
                 canCancel, rendered as a destructive ghost on the left).
  -->
  @if (view() === BookMeetingView.FORM && isView()) {
    <!-- View-mode footer — Space-panel-style: viewer identity card on the
         left, floating action shelf (share + cancel) on the right. The
         shelf is the existing dp-action-shelf component, extended with a
         Cancel slot for this surface. -->
    @if (viewer(); as v) {
      <div class="bmp__viewer-card" data-testid="viewer-card">
        <div class="bmp__viewer-avatar">{{ viewerInitials() }}</div>
        <div class="bmp__viewer-info">
          <span class="bmp__viewer-name">{{ v.name || v.email }}</span>
          @if (v.email) {
            <span class="bmp__viewer-email">{{ v.email }}</span>
          }
        </div>
      </div>
    }
    <dp-action-shelf
      [visible]="true"
      [showFavourite]="false"
      [showCancel]="service.canCancel()"
      (share)="onShareMeeting()"
      (cancel)="cancelMeeting()" />
  } @else if (view() === BookMeetingView.FORM) {
    <footer class="bmp__footer">
      @switch (service.mode()) {
        @case ('create') {
          <!-- Footer summary lives INSIDE the action bar (left side)
               so the viewer can confirm what they're booking at the
               exact moment they click. Conflict count rolls up into
               the same line. Hidden if no tab bar (banner-only modes). -->
          @if (showTabBar() && scheduleV2Enabled) {
            <div class="bmp__footer-inline-summary" role="status" aria-live="polite">
              <bmp-footer-summary
                [date]="service.startTime()"
                [startMin]="footerStartMin()"
                [durationMin]="service.duration() ?? 0"
                [roomCount]="service.rooms().length"
                [peopleCount]="service.attendees().length + 1"
                [teamsLink]="service.isOnlineMeeting()"
                [cateringCount]="footerCateringCount()"
                [cateringTotal]="footerCateringTotal()"
                [conflictCount]="conflictCount()" />
            </div>
          } @else if (service.hasCatering()) {
            <div class="bmp__footer-total">
              <div class="bmp__footer-total-label">{{ 'viewer.book_meeting_panel.services' | transloco }}</div>
              <div class="bmp__footer-total-value">{{ formattedCateringTotal() }}</div>
            </div>
          }
          <div class="bmp__footer-spacer"></div>
          @if (scheduleV2Enabled) {
            <paper-button
              id="bmp-minimise-footer-btn"
              intent="ghost" size="md" shape="icon"
              icon="minus" [iconSize]="14"
              [ariaLabel]="'Minimise draft'"
              [title]="'Minimise draft — keep working elsewhere'"
              data-testid="bmp-minimise-footer-btn"
              (click)="onMinimiseDraft()" />
          }
          <paper-button intent="secondary" size="md" (click)="close()">
            {{ 'viewer.book_meeting_panel.cancel_5' | transloco }}
          </paper-button>
          <paper-button
            intent="primary"
            size="md"
            data-testid="submit-booking-btn"
            [disabled]="!service.canSubmit() || service.isSubmitting()"
            [loading]="service.isSubmitting()"
            (click)="onSubmit()">
            @if (service.isSubmitting()) {
              {{ 'viewer.book_meeting_panel.creating_in_your_calendar' | transloco }}
            } @else if (service.requiresApproval()) {
              {{ 'viewer.book_meeting_panel.send_for_approval' | transloco }}
            } @else {
              {{ 'viewer.book_meeting_panel.book_meeting' | transloco }}
            }
          </paper-button>
        }

        @case ('edit') {
          @if (service.canCancel()) {
            <paper-button
              intent="destructive"
              size="md"
              data-testid="cancel-meeting-btn"
              [disabled]="service.isSubmitting()"
              (click)="cancelMeeting()">
              {{ 'viewer.book_meeting_panel.cancel_meeting' | transloco }}
            </paper-button>
          }
          <div class="bmp__footer-spacer"></div>
          <paper-button
            intent="secondary"
            size="md"
            data-testid="discard-changes-btn"
            [disabled]="service.isSubmitting()"
            (click)="onDiscardChanges()">
            {{ 'viewer.book_meeting_panel.discard' | transloco }}
          </paper-button>
          <paper-button
            intent="primary"
            size="md"
            data-testid="save-changes-btn"
            [disabled]="!service.canSubmit() || service.isSubmitting()"
            [loading]="service.isSubmitting()"
            (click)="onSaveChanges()">
            @if (service.isSubmitting()) { {{ 'viewer.book_meeting_panel.saving' | transloco }} } @else { {{ 'viewer.book_meeting_panel.save_changes' | transloco }} }
          </paper-button>
        }
      }
    </footer>
  }
</div>

<!-- Confirm-on-close dialog. Surfaces only when the user tries to
     close a CREATE-mode panel with unsaved changes. Two destinations:
     discard the draft (red) or save-as-draft (primary, mirrors the
     minimise affordance). Dismissing the dialog (Esc / backdrop) leaves
     the panel open so the user can keep editing. -->
<paper-dialog
  [open]="showCloseConfirm()"
  [title]="'viewer.book_meeting_panel.save_this_draft_for_later' | transloco"
  description="You haven't booked this meeting yet. Save it as a draft to recall later, or discard the changes."
  size="sm"
  data-testid="bmp-close-confirm"
  (closed)="onConfirmCancel()">
  <div slot="footer" class="bmp__confirm-footer">
    <paper-button
      id="bmp-confirm-discard"
      intent="destructive"
      size="sm"
      data-testid="bmp-confirm-discard"
      (click)="onConfirmDiscard()">
      {{ 'viewer.book_meeting_panel.discard_draft' | transloco }}
    </paper-button>
    <paper-button
      id="bmp-confirm-save-draft"
      intent="primary"
      size="sm"
      data-testid="bmp-confirm-save-draft"
      (click)="onConfirmSaveAsDraft()">
      {{ 'viewer.book_meeting_panel.save_as_draft' | transloco }}
    </paper-button>
  </div>
</paper-dialog>

